import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSiteConfig } from '../context/SiteConfigContext';
import axios from 'axios';
import { checkRequired, FieldError, Req, ERROR_COLOR } from '../utils/formErrors';
import FanCarousel from '../components/common/FanCarousel';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/* ── color helpers (from landing-core.jsx) ── */
const _h2 = (h) => { h = h.replace('#',''); return [0,2,4].map(i=>parseInt(h.slice(i,i+2),16)); };
const _2h = (r,g,b) => '#'+[r,g,b].map(v=>Math.max(0,Math.min(255,Math.round(v))).toString(16).padStart(2,'0')).join('');
const mix = (a,b,t) => { const A=_h2(a),B=_h2(b); return _2h(A[0]+(B[0]-A[0])*t,A[1]+(B[1]-A[1])*t,A[2]+(B[2]-A[2])*t); };
export const darken = (h,t) => mix(h,'#0b1411',t);
export const lighten = (h,t) => mix(h,'#ffffff',t);
const hexToRgba = (h,a) => { const [r,g,b]=_h2(h); return `rgba(${r},${g},${b},${a})`; };

// Convertit les liens de partage courants (youtu.be, youtube.com/watch,
// facebook.com/.../videos) en URL "embed" utilisable dans une <iframe> —
// un lien de partage classique est bloqué par YouTube/Facebook en iframe,
// c'est la cause la plus fréquente d'un live qui n'affiche rien.
function toEmbedUrl(url) {
  if (!url) return '';
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') {
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    }
    if (u.hostname.includes('youtube.com')) {
      if (u.pathname === '/watch') {
        const id = u.searchParams.get('v');
        return id ? `https://www.youtube.com/embed/${id}` : url;
      }
      if (u.pathname.startsWith('/live/')) {
        return `https://www.youtube.com/embed/${u.pathname.split('/')[2]}`;
      }
      // déjà /embed/... ou format non reconnu : laisser tel quel
      return url;
    }
    if (u.hostname.includes('facebook.com') && !u.pathname.includes('/plugins/video.php')) {
      return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}`;
    }
    return url;
  } catch {
    return url;
  }
}

export function makeTheme(primary) {
  return {
    primary,
    primaryStrong: darken(primary,0.22),
    primaryDeep:   darken(primary,0.42),
    primarySoft:   lighten(primary,0.9),
    primarySoft2:  lighten(primary,0.82),
    primaryOn:     '#ffffff',
    gradient: `linear-gradient(135deg, ${lighten(primary,0.06)} 0%, ${darken(primary,0.18)} 100%)`,
    ink:'#0f172a', ink2:'#1e2a45', body:'#334155', muted:'#64748b', faint:'#94a3b8',
    line:'#e5e9f2', lineSoft:'#f1f5f9', surface:'#ffffff', app:'#f7f9fb',
    dark: darken(primary,0.62), darkSoft: darken(primary,0.52), gold:'#f6b73c',
  };
}

const CAT = {
  blue:   { a:'#2f5fb0', b:'#1e3a8a', tint:'#eff6ff' },
  green:  { a:'#22a35a', b:'#15803d', tint:'#f0fdf4' },
  purple: { a:'#a855f7', b:'#7e22ce', tint:'#faf5ff' },
  amber:  { a:'#f59e0b', b:'#b45309', tint:'#fffbeb' },
  cyan:   { a:'#0ea5e9', b:'#0369a1', tint:'#f0f9ff' },
};

/* ── hooks ── */
function useMediaQuery(q) {
  const [m,setM] = useState(()=>typeof window!=='undefined'&&window.matchMedia(q).matches);
  useEffect(()=>{const mq=window.matchMedia(q);const fn=()=>setM(mq.matches);fn();mq.addEventListener('change',fn);return()=>mq.removeEventListener('change',fn);},[q]);
  return m;
}
function useInView(opts={threshold:0.3}) {
  const ref=useRef(null);const [seen,setSeen]=useState(false);
  useEffect(()=>{const el=ref.current;if(!el)return;const io=new IntersectionObserver(([e])=>{if(e.isIntersecting){setSeen(true);io.disconnect();}},opts);io.observe(el);return()=>io.disconnect();},[]);
  return [ref,seen];
}
function useCountUp(target,run,dur=1500) {
  const [v,setV]=useState(0);
  useEffect(()=>{if(!run)return;let raf,t0;const ease=x=>1-Math.pow(1-x,3);const step=t=>{if(!t0)t0=t;const p=Math.min(1,(t-t0)/dur);setV(target*ease(p));if(p<1)raf=requestAnimationFrame(step);};raf=requestAnimationFrame(step);return()=>cancelAnimationFrame(raf);},[run,target]);
  return v;
}
function Reveal({ children, className='', style }) {
  const [ref, seen] = useInView({ threshold:0.15 });
  return <div ref={ref} className={`cm-reveal ${seen?'cm-in':''} ${className}`} style={style}>{children}</div>;
}
function useScrolled(threshold=24) {
  const [s,setS]=useState(false);
  useEffect(()=>{const fn=()=>setS(window.scrollY>threshold);fn();window.addEventListener('scroll',fn,{passive:true});return()=>window.removeEventListener('scroll',fn);},[]);
  return s;
}

/* ── icons ── */
const _ico = (paths) => ({ size=24, stroke=1.7, color='currentColor', style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={style}>{paths}</svg>
);
const IconUsers    = _ico(<><path d="M16 19c0-2.5-1.8-4-4-4s-4 1.5-4 4"/><circle cx="12" cy="8.5" r="3"/><path d="M19.5 18.5c0-1.8-1-3-2.6-3.4"/><path d="M17.4 9.4a2.6 2.6 0 0 0-.4-5.1"/><path d="M4.5 18.5c0-1.8 1-3 2.6-3.4"/></>);
const IconQr       = _ico(<><rect x="3.5" y="3.5" width="6" height="6" rx="1.2"/><rect x="14.5" y="3.5" width="6" height="6" rx="1.2"/><rect x="3.5" y="14.5" width="6" height="6" rx="1.2"/><path d="M14.5 14.5h2.5v2.5M20.5 14.5v0M14.5 20.5h2.5M20.5 17.5v3"/></>);
const IconCalendar = _ico(<><rect x="3.5" y="5" width="17" height="15.5" rx="2.2"/><path d="M3.5 9.5h17M8 3.2v3.4M16 3.2v3.4"/><path d="M7.5 13h2M11 13h2M14.5 13h2M7.5 16.5h2M11 16.5h2"/></>);
const IconChart    = _ico(<><path d="M4 4v15.5a1 1 0 0 0 1 1h15.5"/><path d="M8 16l3.2-3.6 2.6 2L19 8.5"/><path d="M19 8.5h-2.6M19 8.5v2.6"/></>);
const IconArrow    = _ico(<><path d="M5 12h13.5"/><path d="M13 6.5l5.5 5.5L13 17.5"/></>);
const IconCheck    = _ico(<><path d="M5 12.5l4.2 4.2L19 7"/></>);
const IconMenu     = _ico(<><path d="M4 7h16M4 12h16M4 17h16"/></>);
const IconClose    = _ico(<><path d="M6 6l12 12M18 6L6 18"/></>);
const IconScan     = _ico(<><path d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M8 20H6a2 2 0 0 1-2-2v-2"/><path d="M4 12h16"/></>);
const IconGlobe    = _ico(<><circle cx="12" cy="12" r="8.4"/><path d="M3.6 12h16.8M12 3.6c2.3 2.3 3.4 5.2 3.4 8.4S14.3 18.1 12 20.4c-2.3-2.3-3.4-5.2-3.4-8.4S9.7 5.9 12 3.6z"/></>);
const IconHands    = _ico(<><path d="M7 13.5l-2-2a1.5 1.5 0 0 1 2-2l3 3"/><path d="M17 13.5l2-2a1.5 1.5 0 0 0-2-2l-3 3"/><path d="M9 11l1.5-3.2a1.4 1.4 0 0 1 2.6 0L14.5 11"/><path d="M7 13.5c0 3 2 5 5 5s5-2 5-5"/></>);
const IconHeart    = _ico(<><path d="M12 20s-7-4.3-7-9.4A3.8 3.8 0 0 1 12 8a3.8 3.8 0 0 1 7-2.4c0 5.1-7 9.4-7 9.4z"/></>);
const IconBook     = _ico(<><path d="M12 6.5C10.5 5 8.2 4.5 5 5v12.5c3.2-.5 5.5 0 7 1.5 1.5-1.5 3.8-2 7-1.5V5c-3.2-.5-5.5 0-7 1.5z"/><path d="M12 6.5V19"/></>);
const IconMusic    = _ico(<><path d="M9 17.5V6.2l9-1.7v11"/><circle cx="6.5" cy="17.5" r="2.5"/><circle cx="15.5" cy="15.5" r="2.5"/></>);
const IconBell     = _ico(<><path d="M18 16.5V11a6 6 0 0 0-12 0v5.5L4.5 18.5h15z"/><path d="M10 21.5a2.2 2.2 0 0 0 4 0"/></>);
const IconStar = ({ size=18, color='#f6b73c' }) => <svg width={size} height={size} viewBox="0 0 24 24" fill={color}><path d="M12 3.2l2.6 5.3 5.8.8-4.2 4.1 1 5.8L12 16.9 6.8 19.2l1-5.8L3.6 9.3l5.8-.8z"/></svg>;

const FEAT_ICON_MAP = { IconUsers, IconQr, IconCalendar, IconChart, IconHands, IconHeart, IconBook, IconMusic, IconGlobe };
const ACT_ICON_MAP  = { IconHands, IconBook, IconMusic, IconHeart, IconCalendar, IconChart };

/* ── primitives ── */
function Btn({ children, variant='primary', theme, size='md', icon, onClick, style }) {
  const [h,setH] = useState(false);
  const pad = size==='lg'?'15px 26px':size==='sm'?'9px 16px':'12px 22px';
  const fs = size==='lg'?16.5:size==='sm'?14:15.5;
  const base = { display:'inline-flex', alignItems:'center', gap:9, padding:pad, fontSize:fs, fontWeight:600, borderRadius:12, cursor:'pointer', textDecoration:'none', letterSpacing:'-0.01em', border:'1px solid transparent', whiteSpace:'nowrap', transition:'transform .18s, box-shadow .18s, background .18s', transform:h?'translateY(-1px)':'none', fontFamily:'inherit', lineHeight:1 };
  const skins = {
    primary: { background:theme.primary, color:theme.primaryOn, boxShadow:h?`0 10px 22px ${theme.primary}55`:`0 4px 12px ${theme.primary}38` },
    dark:    { background:theme.ink, color:'#fff', boxShadow:h?'0 10px 22px rgba(15,23,42,.28)':'0 4px 12px rgba(15,23,42,.18)' },
    ghost:   { background:h?theme.lineSoft:'transparent', color:theme.ink2, border:`1px solid ${theme.line}` },
    white:   { background:'#fff', color:theme.primaryStrong, boxShadow:h?'0 10px 22px rgba(15,23,42,.18)':'0 4px 12px rgba(15,23,42,.10)' },
    outlineLight: { background:h?'rgba(255,255,255,.12)':'transparent', color:'#fff', border:'1px solid rgba(255,255,255,.45)' },
  };
  return (
    <span onClick={onClick} onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)} style={{ ...base, ...skins[variant], ...style }}>
      {children}{icon&&<IconArrow size={18} stroke={2}/>}
    </span>
  );
}

function Eyebrow({ children, theme, color }) {
  return <div style={{ display:'inline-flex', alignItems:'center', gap:8, fontSize:12.5, fontWeight:700, letterSpacing:'0.14em', textTransform:'uppercase', color:color||theme.primaryStrong }}>
    <span style={{ width:22, height:1.5, background:'currentColor', opacity:.5, borderRadius:2 }}/>{children}
  </div>;
}

const SECTION_MAX = 1180;
export function Container({ children, style }) {
  return <div style={{ width:'100%', maxWidth:SECTION_MAX, margin:'0 auto', padding:'0 24px', boxSizing:'border-box', ...style }}>{children}</div>;
}

function SectionHead({ eyebrow, title, subtitle, theme, mobile, center, titleColor, subtitleColor }) {
  return <div style={{ maxWidth:640, margin:center?'0 auto':0, textAlign:center?'center':'left' }}>
    <div style={{ display:'flex', justifyContent:center?'center':'flex-start' }}><Eyebrow theme={theme}>{eyebrow}</Eyebrow></div>
    <h2 style={{ fontSize:mobile?30:40, fontWeight:800, letterSpacing:'-0.03em', color:titleColor||theme.ink, margin:'16px 0 0', lineHeight:1.1 }}>{title}</h2>
    {subtitle&&<p style={{ fontSize:mobile?16:18, lineHeight:1.6, color:subtitleColor||theme.body, margin:'14px 0 0' }}>{subtitle}</p>}
  </div>;
}

/* ── Logo ── */
function Logo({ theme, size=38 }) {
  return <span style={{ display:'grid', placeItems:'center', width:size, height:size, borderRadius:11, background:theme.gradient, boxShadow:`0 6px 14px ${theme.primary}40` }}>
    <svg width={size*0.56} height={size*0.56} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v6M9 6h6"/><path d="M5 21v-9l7-4 7 4v9"/><path d="M10 21v-4a2 2 0 0 1 4 0v4"/>
    </svg>
  </span>;
}

/* ── QR glyph ── */
function QrGlyph({ color }) {
  return <svg viewBox="0 0 40 40" width="100%" height="100%" fill={color}>
    <rect x="0" y="0" width="13" height="13" rx="2"/><rect x="27" y="0" width="13" height="13" rx="2"/><rect x="0" y="27" width="13" height="13" rx="2"/>
    <rect x="4" y="4" width="5" height="5" fill="#fff"/><rect x="31" y="4" width="5" height="5" fill="#fff"/><rect x="4" y="31" width="5" height="5" fill="#fff"/>
    <rect x="18" y="0" width="4" height="4"/><rect x="18" y="8" width="4" height="4"/><rect x="18" y="18" width="4" height="4"/>
    <rect x="27" y="18" width="4" height="4"/><rect x="36" y="18" width="4" height="4"/><rect x="27" y="27" width="4" height="4"/>
    <rect x="33" y="33" width="7" height="7"/><rect x="18" y="33" width="4" height="4"/>
  </svg>;
}

/* ── Navbar ── */
export function Navbar({ config, theme, mobile, onLogin, onFidele, user, navigate, hasEvents, hasGallery }) {
  const scrolled = useScrolled(24);
  const [open, setOpen] = useState(false);
  const isGalleryPage = typeof window !== 'undefined' && window.location.pathname === '/galerie-publique';
  const solid = scrolled || open || isGalleryPage;
  const links = [
    { label:'Accueil', href:'/' },
    { label:'Fonctionnalités', href: isGalleryPage ? '/#features' : '#features' },
    { label:'Activités', href: isGalleryPage ? '/#activites' : '#activites' },
    ...(hasEvents ? [{ label:'Événements', href: isGalleryPage ? '/#evenements' : '#evenements' }] : []),
    ...(hasGallery ? [{ label:'Galerie', href:'/galerie-publique' }] : []),
    { label:'Témoignages', href: isGalleryPage ? '/#temoignages' : '#temoignages' },
  ];
  return (
    <header style={{ position:'fixed', top:0, left:0, right:0, zIndex:100, background:solid?'rgba(255,255,255,0.88)':'transparent', backdropFilter:solid?'saturate(160%) blur(14px)':'none', WebkitBackdropFilter:solid?'saturate(160%) blur(14px)':'none', borderBottom:`1px solid ${solid?theme.line:'transparent'}`, transition:'background .3s, border-color .3s, box-shadow .3s', boxShadow:scrolled?'0 2px 18px rgba(15,23,42,.05)':'none' }}>
      <Container style={{ display:'flex', alignItems:'center', height:70, gap:24 }}>
        <span onClick={()=>navigate('/')} style={{ display:'flex', alignItems:'center', gap:11, textDecoration:'none', marginRight:'auto', cursor:'pointer' }}>
          {config.logoUrl ? <img src={config.logoUrl} alt="logo" style={{ width:36, height:36, borderRadius:9, objectFit:'cover' }}/> : <Logo theme={theme}/>}
          <span style={{ fontSize:19, fontWeight:700, color:theme.ink, letterSpacing:'-0.02em' }}>{config.brand||'RCC'}</span>
        </span>
        {!mobile && (
          <nav style={{ display:'flex', alignItems:'center', gap:30 }}>
            {links.map(l => <NavLink key={l.label} label={l.label} href={l.href} theme={theme}/>)}
          </nav>
        )}
        {!mobile ? (
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            {/* RCC-UX-001 : point d'entrée fidèle mis en avant, distinct des liens */}
            <span title="Ma carte, mes activités et mes présences" style={{ display:'inline-flex' }}>
              <Btn theme={theme} variant="primary" size="sm" onClick={onFidele}>Espace fidèle</Btn>
            </span>
            <Btn theme={theme} variant="ghost" size="sm" onClick={user?()=>navigate('/dashboard'):onLogin}>{user?'Dashboard':'Se connecter'}</Btn>
          </div>
        ) : (
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <Btn theme={theme} variant="primary" size="sm" onClick={onFidele}>Espace fidèle</Btn>
            <button onClick={()=>setOpen(o=>!o)} style={{ display:'grid', placeItems:'center', width:42, height:42, borderRadius:11, border:`1px solid ${theme.line}`, background:'#fff', color:theme.ink, cursor:'pointer' }}>
              {open?<IconClose size={22}/>:<IconMenu size={22}/>}
            </button>
          </div>
        )}
      </Container>
      {mobile && open && (
        <div style={{ borderTop:`1px solid ${theme.line}`, background:'#fff', padding:'14px 24px 20px' }}>
          {links.map(l=><a key={l.label} href={l.href} onClick={()=>setOpen(false)} style={{ display:'block', padding:'11px 0', fontSize:16, fontWeight:600, color:theme.ink2, textDecoration:'none', borderBottom:`1px solid ${theme.lineSoft}` }}>{l.label}</a>)}
          <div style={{ display:'flex', gap:10, marginTop:14 }}>
            <Btn theme={theme} variant="primary" size="sm" style={{ flex:1, justifyContent:'center' }} onClick={onFidele}>Espace fidèle</Btn>
            <Btn theme={theme} variant="ghost" size="sm" style={{ flex:1, justifyContent:'center' }} onClick={user?()=>navigate('/dashboard'):onLogin}>{user?'Dashboard':'Se connecter'}</Btn>
          </div>
        </div>
      )}
    </header>
  );
}

function NavLink({ label, href, theme }) {
  const [h,setH] = useState(false);
  return <a href={href} onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)} style={{ position:'relative', fontSize:15, fontWeight:600, color:h?theme.ink:theme.body, textDecoration:'none', transition:'color .18s' }}>
    {label}
    <span style={{ position:'absolute', left:0, right:0, bottom:-6, height:2, borderRadius:2, background:theme.primary, transform:`scaleX(${h?1:0})`, transformOrigin:'left', transition:'transform .22s cubic-bezier(.4,0,.2,1)' }}/>
  </a>;
}

/* ── Phone Mockup — Portail Fidèle ── */
function PhoneMockup({ theme, landingPhone }) {
  const p = landingPhone || {};
  const church = p.church || 'RCC';
  const member = p.member || 'Jonathan Touré';
  const role   = p.role   || 'Membre actif';
  const activities = p.activities || [
    { name:'Culte du dimanche', time:'Dim. · 09h00', cat:'blue' },
    { name:'Cellule de prière',  time:'Mar. · 19h00', cat:'amber' },
    { name:'Groupe de louange',  time:'Ven. · 18h30', cat:'purple' },
  ];
  const ICON_ACT = { blue:IconHands, amber:IconHeart, purple:IconMusic, green:IconBook, cyan:IconCalendar };
  return (
    <div style={{ position:'relative', width:300, animation:'cmFloat 6s ease-in-out infinite', flexShrink:0 }}>
      <div style={{ position:'absolute', inset:'-12% -8% -4%', background:`radial-gradient(60% 50% at 50% 30%, ${theme.primary}33, transparent 70%)`, filter:'blur(28px)', zIndex:0 }}/>
      <div style={{ position:'relative', zIndex:1, width:300, height:612, borderRadius:44, background:'#0b1411', padding:11, boxShadow:'0 40px 80px -24px rgba(15,23,42,.5), 0 0 0 1px rgba(15,23,42,.6)' }}>
        <div style={{ position:'relative', width:'100%', height:'100%', borderRadius:34, background:theme.app, overflow:'hidden' }}>
          {/* notch */}
          <div style={{ position:'absolute', top:0, left:'50%', transform:'translateX(-50%)', width:116, height:26, background:'#0b1411', borderRadius:'0 0 16px 16px', zIndex:5 }}/>
          {/* status bar */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'13px 22px 0', fontSize:12.5, fontWeight:700, color:theme.ink }}>
            <span>9:41</span>
            <span style={{ display:'flex', gap:4, alignItems:'center' }}>
              <svg width="17" height="11" viewBox="0 0 17 11" fill={theme.ink}><rect x="0" y="6" width="3" height="5" rx="1"/><rect x="4.5" y="4" width="3" height="7" rx="1"/><rect x="9" y="2" width="3" height="9" rx="1"/><rect x="13.5" y="0" width="3" height="11" rx="1"/></svg>
              <svg width="16" height="11" viewBox="0 0 16 11" fill={theme.ink}><rect x="1" y="1" width="12" height="9" rx="2" stroke={theme.ink} fill="none"/><rect x="3" y="3" width="8" height="5" rx="1"/><rect x="14" y="3.5" width="1.5" height="4" rx="1"/></svg>
            </span>
          </div>
          <div style={{ padding:'16px 16px 0' }}>
            {/* Carte membre */}
            <div style={{ position:'relative', borderRadius:22, background:theme.gradient, padding:18, color:'#fff', overflow:'hidden', boxShadow:`0 14px 26px -10px ${theme.primary}88` }}>
              <div style={{ position:'absolute', right:-30, top:-30, width:120, height:120, borderRadius:'50%', background:'rgba(255,255,255,.10)' }}/>
              <div style={{ position:'relative', display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
                <span style={{ fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', opacity:.85 }}>Carte de membre</span>
                <span style={{ fontSize:11.5, fontWeight:700, opacity:.9 }}>{church}</span>
              </div>
              <div style={{ position:'relative', display:'flex', gap:13, alignItems:'center' }}>
                <div style={{ width:48, height:48, borderRadius:14, background:'rgba(255,255,255,.22)', display:'grid', placeItems:'center', fontSize:17, fontWeight:700 }}>
                  {member.split(' ').map(w=>w[0]).slice(0,2).join('')}
                </div>
                <div>
                  <div style={{ fontSize:16.5, fontWeight:700, lineHeight:1.2 }}>{member}</div>
                  <div style={{ display:'inline-flex', alignItems:'center', gap:5, marginTop:5, fontSize:11.5, fontWeight:600, padding:'3px 9px', borderRadius:999, background:'rgba(255,255,255,.20)' }}>
                    <span style={{ width:6, height:6, borderRadius:'50%', background:'#9bf3b6' }}/>{role}
                  </div>
                </div>
                <div style={{ marginLeft:'auto', width:50, height:50, borderRadius:11, background:'#fff', padding:5 }}>
                  <QrGlyph color={theme.primaryStrong}/>
                </div>
              </div>
            </div>
            {/* Activités */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', margin:'18px 2px 10px' }}>
              <span style={{ fontSize:14.5, fontWeight:700, color:theme.ink }}>Mes activités</span>
              <span style={{ fontSize:12, fontWeight:600, color:theme.primaryStrong }}>Tout voir</span>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:9 }}>
              {activities.map((a,i) => {
                const cc = CAT[a.cat] || CAT.blue;
                const IcoA = ICON_ACT[a.cat] || IconHands;
                return (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:11, background:'#fff', border:`1px solid ${theme.line}`, borderRadius:15, padding:'10px 12px' }}>
                    <span style={{ display:'grid', placeItems:'center', width:34, height:34, borderRadius:10, background:cc.tint, color:cc.b }}>
                      <IcoA size={18} stroke={1.9} color={cc.b}/>
                    </span>
                    <div style={{ minWidth:0 }}>
                      <div style={{ fontSize:13.5, fontWeight:600, color:theme.ink2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{a.name}</div>
                      <div style={{ fontSize:11.5, color:theme.muted, marginTop:1 }}>{a.time}</div>
                    </div>
                    <IconArrow size={15} color={theme.faint} style={{ marginLeft:'auto' }}/>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Bouton scan */}
          <div style={{ position:'absolute', left:16, right:16, bottom:18 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10, padding:14, borderRadius:16, background:theme.ink, color:'#fff', fontSize:14.5, fontWeight:700, boxShadow:'0 12px 24px -8px rgba(15,23,42,.6)' }}>
              <IconScan size={19} stroke={2}/> Scanner ma présence
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Login Modal ── */
function LoginModal({ onClose, onSuccess, destination, theme }) {
  const { login } = useAuth();
  const [step,setStep] = useState('church');
  const [churches,setChurches] = useState([]);
  const [loading2,setLoading2] = useState(true);
  const [selected,setSelected] = useState(null);
  const [username,setUsername] = useState('');
  const [password,setPassword] = useState('');
  const [loading,setLoading] = useState(false);
  const [error,setError] = useState('');
  useEffect(()=>{ axios.get(`${API}/churches/public/`).then(r=>setChurches(Array.isArray(r.data)?r.data:[])).catch(()=>{}).finally(()=>setLoading2(false)); },[]);
  const [loginErrors,setLoginErrors] = useState({});
  const handleLogin = async e => {
    e.preventDefault();
    const found=checkRequired({username,password},{username:'Identifiant',password:'Mot de passe'});
    setLoginErrors(found); if(Object.keys(found).length)return;
    setLoading(true);setError('');
    try{await login(username.trim(),password);onSuccess();}
    catch{setError('Identifiants incorrects.');}
    finally{setLoading(false);}
  };
  const inp={width:'100%',padding:'11px 14px',border:`1.5px solid ${theme.line}`,borderRadius:10,fontSize:14.5,outline:'none',boxSizing:'border-box',color:theme.ink,background:theme.app,fontFamily:'inherit',transition:'border-color .15s'};
  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(15,23,42,.65)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',padding:20,backdropFilter:'blur(10px)' }} onClick={onClose}>
      <div style={{ background:'#fff',borderRadius:24,width:'100%',maxWidth:420,boxShadow:'0 32px 80px rgba(15,23,42,.25)',overflow:'hidden' }} onClick={e=>e.stopPropagation()}>
        <div style={{ background:theme.gradient,padding:'22px 24px 18px',position:'relative' }}>
          <div style={{ position:'absolute',top:-20,right:-20,width:100,height:100,borderRadius:'50%',background:'rgba(255,255,255,.08)' }}/>
          <button onClick={onClose} style={{ position:'absolute',top:12,right:12,background:'rgba(255,255,255,.2)',border:'none',borderRadius:8,width:30,height:30,cursor:'pointer',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center' }}><IconClose size={14} color="#fff"/></button>
          <div style={{ color:'rgba(255,255,255,.7)',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:4 }}>{destination==='admin'?'Administration':'Accueil Entrée'}</div>
          <div style={{ color:'#fff',fontWeight:800,fontSize:18 }}>{step==='church'?'Choisissez votre église':selected?.name}</div>
        </div>
        <div style={{ padding:'20px 24px 24px' }}>
          {step==='church'?(
            <>
              <p style={{ color:theme.body,fontSize:14,margin:'0 0 14px',lineHeight:1.6 }}>Sélectionnez votre église pour continuer</p>
              {loading2?<div style={{ textAlign:'center',padding:24,color:theme.muted }}>Chargement…</div>
              :<div style={{ display:'flex',flexDirection:'column',gap:8,maxHeight:240,overflowY:'auto' }}>
                {churches.map(ch=>(
                  <button key={ch.id} onClick={()=>{setSelected(ch);setStep('login');}}
                    style={{ display:'flex',alignItems:'center',gap:10,padding:'11px 13px',borderRadius:12,border:`1.5px solid ${theme.line}`,background:'#fff',cursor:'pointer',textAlign:'left',width:'100%',transition:'all .15s' }}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=theme.primary;e.currentTarget.style.background=theme.app;}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=theme.line;e.currentTarget.style.background='#fff';}}>
                    {ch.logo_url?<img src={ch.logo_url} alt="" style={{ width:36,height:36,borderRadius:8,objectFit:'cover',flexShrink:0 }}/>
                      :<div style={{ width:36,height:36,borderRadius:8,background:ch.primary_color||theme.primary,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700,flexShrink:0 }}>{ch.name[0]}</div>}
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700,fontSize:14,color:theme.ink }}>{ch.name}</div>
                      {ch.city&&<div style={{ fontSize:12,color:theme.muted }}>{ch.city}</div>}
                    </div>
                  </button>
                ))}
              </div>}
            </>
          ):(
            <>
              <button onClick={()=>{setStep('church');setError('');}} style={{ display:'flex',alignItems:'center',gap:6,background:'none',border:'none',color:theme.muted,fontSize:13,cursor:'pointer',marginBottom:14,padding:0 }}>
                <IconArrow size={14} color={theme.muted} style={{ transform:'rotate(180deg)' }}/> Changer d'église
              </button>
              <form onSubmit={handleLogin} noValidate style={{ display:'flex',flexDirection:'column',gap:12 }}>
                <div>
                  <label style={{ display:'block',fontSize:13,fontWeight:600,color:theme.ink2,marginBottom:5 }}>Identifiant<Req /></label>
                  <input type="text" autoFocus value={username} onChange={e=>setUsername(e.target.value)} placeholder="Nom d'utilisateur" style={{...inp,...(loginErrors.username?{borderColor:ERROR_COLOR}:{})}}
                    onFocus={e=>e.target.style.borderColor=theme.primary} onBlur={e=>e.target.style.borderColor=theme.line}/>
                  <FieldError message={loginErrors.username}/>
                </div>
                <div>
                  <label style={{ display:'block',fontSize:13,fontWeight:600,color:theme.ink2,marginBottom:5 }}>Mot de passe<Req /></label>
                  <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" style={{...inp,...(loginErrors.password?{borderColor:ERROR_COLOR}:{})}}
                    onFocus={e=>e.target.style.borderColor=theme.primary} onBlur={e=>e.target.style.borderColor=theme.line}/>
                  <FieldError message={loginErrors.password}/>
                </div>
                {error&&<div style={{ background:'#fef2f2',border:'1px solid #fecaca',borderRadius:8,padding:'8px 12px',color:'#dc2626',fontSize:13 }}>{error}</div>}
                <button type="submit" disabled={loading} style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:9,padding:'12px 22px',fontSize:15.5,fontWeight:600,borderRadius:12,cursor:loading?'not-allowed':'pointer',border:'1px solid transparent',background:theme.primary,color:theme.primaryOn||'#fff',boxShadow:`0 4px 12px ${theme.primary}38`,marginTop:4,fontFamily:'inherit',lineHeight:1,opacity:loading?.7:1,width:'100%' }}>
                  {loading?'Connexion…':(destination==='admin'?'Accéder au dashboard':"Accéder à l'accueil")}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Contact Modal ── */
function ContactModal({ subjectType, onClose, theme }) {
  const [form, setForm] = useState({ name:'', email:'', phone:'', church_name:'', city:'', message:'' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const inp = { width:'100%', padding:'11px 14px', border:`1.5px solid ${theme.line}`, borderRadius:10, fontSize:14.5, outline:'none', boxSizing:'border-box', color:theme.ink, background:theme.app, fontFamily:'inherit', transition:'border-color .15s' };
  const isCreation = subjectType === 'creation_espace';
  const [contactErrors, setContactErrors] = useState({});

  const handleSubmit = async e => {
    e.preventDefault();
    const found = checkRequired(form, { name: 'Nom complet', email: 'Email' });
    setContactErrors(found);
    if (Object.keys(found).length) return;
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/notifications/contact/`, { subject_type: subjectType, ...form });
      setDone(true);
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(15,23,42,.65)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',padding:20,backdropFilter:'blur(10px)' }} onClick={onClose}>
      <div style={{ background:'#fff',borderRadius:24,width:'100%',maxWidth:460,boxShadow:'0 32px 80px rgba(15,23,42,.25)',overflow:'hidden',maxHeight:'90vh',display:'flex',flexDirection:'column' }} onClick={e=>e.stopPropagation()}>
        <div style={{ background:theme.gradient,padding:'22px 24px 18px',position:'relative',flexShrink:0 }}>
          <div style={{ position:'absolute',top:-20,right:-20,width:100,height:100,borderRadius:'50%',background:'rgba(255,255,255,.08)' }}/>
          <button onClick={onClose} style={{ position:'absolute',top:12,right:12,background:'rgba(255,255,255,.2)',border:'none',borderRadius:8,width:30,height:30,cursor:'pointer',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center' }}><IconClose size={14} color="#fff"/></button>
          <div style={{ color:'rgba(255,255,255,.7)',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:4 }}>{isCreation?'Nouvel espace':'Contact'}</div>
          <div style={{ color:'#fff',fontWeight:800,fontSize:18 }}>{isCreation?'Créer mon espace église':"Parler à l'équipe"}</div>
        </div>
        <div style={{ padding:'20px 24px 24px', overflowY:'auto' }}>
          {done ? (
            <div style={{ textAlign:'center', padding:'24px 0' }}>
              <div style={{ fontSize:40, marginBottom:12 }}>✅</div>
              <div style={{ fontWeight:700, fontSize:16, color:theme.ink, marginBottom:6 }}>Merci !</div>
              <p style={{ color:theme.body, fontSize:14, lineHeight:1.6, margin:0 }}>Votre demande a bien été envoyée. Notre équipe vous recontactera rapidement.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <p style={{ color:theme.body, fontSize:14, margin:0, lineHeight:1.6 }}>
                {isCreation ? "Donnez-nous quelques informations sur votre église et nous vous recontacterons pour créer votre espace." : "Une question, une suggestion ? Écrivez-nous, nous vous répondrons rapidement."}
              </p>
              <div>
                <label style={{ display:'block',fontSize:13,fontWeight:600,color:theme.ink2,marginBottom:5 }}>Nom complet<Req /></label>
                <input style={{...inp,...(contactErrors.name?{borderColor:ERROR_COLOR}:{})}} value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Jean Dupont"
                  onFocus={e=>e.target.style.borderColor=theme.primary} onBlur={e=>e.target.style.borderColor=theme.line}/>
                <FieldError message={contactErrors.name}/>
              </div>
              <div>
                <label style={{ display:'block',fontSize:13,fontWeight:600,color:theme.ink2,marginBottom:5 }}>Email<Req /></label>
                <input type="email" style={{...inp,...(contactErrors.email?{borderColor:ERROR_COLOR}:{})}} value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="jean@email.com"
                  onFocus={e=>e.target.style.borderColor=theme.primary} onBlur={e=>e.target.style.borderColor=theme.line}/>
                <FieldError message={contactErrors.email}/>
              </div>
              <div>
                <label style={{ display:'block',fontSize:13,fontWeight:600,color:theme.ink2,marginBottom:5 }}>Téléphone</label>
                <input style={inp} value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} placeholder="+225 07 XX XX XX"
                  onFocus={e=>e.target.style.borderColor=theme.primary} onBlur={e=>e.target.style.borderColor=theme.line}/>
              </div>
              {isCreation && (
                <>
                  <div>
                    <label style={{ display:'block',fontSize:13,fontWeight:600,color:theme.ink2,marginBottom:5 }}>Nom de l'église</label>
                    <input style={inp} value={form.church_name} onChange={e=>setForm({...form,church_name:e.target.value})} placeholder="Église Royal Centre Chrétien"
                      onFocus={e=>e.target.style.borderColor=theme.primary} onBlur={e=>e.target.style.borderColor=theme.line}/>
                  </div>
                  <div>
                    <label style={{ display:'block',fontSize:13,fontWeight:600,color:theme.ink2,marginBottom:5 }}>Ville</label>
                    <input style={inp} value={form.city} onChange={e=>setForm({...form,city:e.target.value})} placeholder="Abidjan"
                      onFocus={e=>e.target.style.borderColor=theme.primary} onBlur={e=>e.target.style.borderColor=theme.line}/>
                  </div>
                </>
              )}
              <div>
                <label style={{ display:'block',fontSize:13,fontWeight:600,color:theme.ink2,marginBottom:5 }}>Message</label>
                <textarea rows={3} style={{...inp, resize:'vertical'}} value={form.message} onChange={e=>setForm({...form,message:e.target.value})} placeholder="Votre message…"
                  onFocus={e=>e.target.style.borderColor=theme.primary} onBlur={e=>e.target.style.borderColor=theme.line}/>
              </div>
              {error && <div style={{ background:'#fef2f2',border:'1px solid #fecaca',borderRadius:8,padding:'8px 12px',color:'#dc2626',fontSize:13 }}>{error}</div>}
              <button type="submit" disabled={loading} style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:9,padding:'12px 22px',fontSize:15.5,fontWeight:600,borderRadius:12,cursor:loading?'not-allowed':'pointer',border:'1px solid transparent',background:theme.primary,color:theme.primaryOn||'#fff',boxShadow:`0 4px 12px ${theme.primary}38`,marginTop:4,fontFamily:'inherit',lineHeight:1,opacity:loading?.7:1,width:'100%' }}>
                {loading?'Envoi…':'Envoyer ma demande'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Activity Modal ── */
function ActivityModal({ act, onClose, theme, user, navigate, onLoginRequest }) {
  const labels    = { planned:'Planifiée', ongoing:'En cours', completed:'Terminée' };
  const statusClr = { planned:`${theme.primary}20`, ongoing:'#dcfce7', completed:'#f1f5f9' };
  const statusTxt = { planned:theme.primaryStrong, ongoing:'#16a34a', completed:theme.muted };
  const color = act.activity_type_color || theme.primary;
  const fmt   = d => { try{return new Date(d+'T00:00:00').toLocaleDateString('fr-FR',{weekday:'long',day:'numeric',month:'long',year:'numeric'});}catch{return d;} };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(15,23,42,.7)',zIndex:400,display:'flex',alignItems:'flex-end',justifyContent:'center',backdropFilter:'blur(10px)' }}
      onClick={onClose}>
      <div style={{ background:'#fff',borderRadius:'24px 24px 0 0',width:'100%',maxWidth:520,boxShadow:'0 -8px 60px rgba(15,23,42,.25)',overflow:'hidden',maxHeight:'90vh',display:'flex',flexDirection:'column' }}
        onClick={e=>e.stopPropagation()}>

        {/* ── Header avec couleur de l'activité ── */}
        <div style={{ background:`linear-gradient(135deg,${color},${darken(color,0.3)})`,padding:'22px 22px 20px',position:'relative',flexShrink:0 }}>
          <button onClick={onClose} style={{ position:'absolute',top:12,right:12,background:'rgba(255,255,255,.2)',border:'none',borderRadius:8,width:32,height:32,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}>
            <IconClose size={14} color="#fff"/>
          </button>
          {/* Badges */}
          <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:10,flexWrap:'wrap' }}>
            <span style={{ display:'inline-flex',alignItems:'center',gap:5,background:'rgba(255,255,255,.22)',borderRadius:999,padding:'4px 12px',fontSize:11,fontWeight:700,color:'#fff' }}>
              {act.status==='ongoing'&&<span style={{ width:6,height:6,borderRadius:'50%',background:'#4ade80',display:'inline-block',animation:'pulse 1.5s infinite' }}/>}
              {labels[act.status]}
            </span>
            {act.activity_type&&<span style={{ background:'rgba(255,255,255,.18)',borderRadius:999,padding:'4px 12px',fontSize:11,fontWeight:700,color:'rgba(255,255,255,.9)' }}>{act.activity_type}</span>}
          </div>
          <h2 style={{ color:'#fff',fontWeight:900,fontSize:22,margin:'0 0 6px',lineHeight:1.2,letterSpacing:'-0.02em' }}>{act.name}</h2>
          {act.church_name&&(
            <div style={{ color:'rgba(255,255,255,.75)',fontSize:13,display:'flex',alignItems:'center',gap:5 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.75)" strokeWidth="2"><path d="M3 9l9-7 9 7v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"/><rect x="9" y="14" width="6" height="6" rx="0.5"/></svg>
              {act.church_name}
            </div>
          )}
        </div>

        {/* ── Corps scrollable ── */}
        <div style={{ padding:'20px 22px',overflowY:'auto',flex:1 }}>

          {/* Infos clés */}
          <div style={{ display:'flex',flexDirection:'column',gap:10,marginBottom:16 }}>
            {/* Date */}
            <div style={{ display:'flex',alignItems:'center',gap:12 }}>
              <div style={{ width:40,height:40,borderRadius:12,background:`${color}12`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                <IconCalendar size={18} color={color}/>
              </div>
              <div>
                <div style={{ fontSize:10,color:theme.muted,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:2 }}>Date</div>
                <div style={{ fontWeight:700,fontSize:15,color:theme.ink }}>{fmt(act.date)}</div>
                {act.start_time&&(
                  <div style={{ fontSize:13,color:theme.muted,marginTop:1 }}>
                    {act.start_time.slice(0,5)}{act.end_time?` → ${act.end_time.slice(0,5)}`:''}</div>
                )}
              </div>
            </div>
            {/* Lieu */}
            {act.location&&(
              <div style={{ display:'flex',alignItems:'center',gap:12 }}>
                <div style={{ width:40,height:40,borderRadius:12,background:`${color}12`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                </div>
                <div>
                  <div style={{ fontSize:10,color:theme.muted,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:2 }}>Lieu</div>
                  <div style={{ fontWeight:700,fontSize:15,color:theme.ink }}>{act.location}</div>
                </div>
              </div>
            )}
          </div>

          {/* Description complète */}
          {act.description && (
            <div style={{ background:`${color}08`,borderRadius:14,padding:'14px 16px',marginBottom:18,border:`1px solid ${color}18` }}>
              <div style={{ fontSize:10,color:color,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8,display:'flex',alignItems:'center',gap:6 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                Description
              </div>
              <p style={{ fontSize:14.5,color:theme.body,lineHeight:1.75,margin:0 }}>{act.description}</p>
            </div>
          )}

          {/* ── Actions ── */}
          <div style={{ display:'flex',flexDirection:'column',gap:10 }}>
            {user ? (
              /* Connecté → aller dans le dashboard */
              <button onClick={()=>{onClose();navigate(`/activites/${act.id}`);}}
                style={{ width:'100%',padding:'14px',background:`linear-gradient(135deg,${color},${darken(color,0.25)})`,color:'white',border:'none',borderRadius:14,fontWeight:800,fontSize:15,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:8,boxShadow:`0 4px 16px ${color}40` }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                Voir les détails complets
              </button>
            ) : (
              /* Non connecté → invitation à se connecter */
              <div style={{ borderRadius:16,overflow:'hidden',border:`1.5px solid ${color}25` }}>
                {/* Bandeau info */}
                <div style={{ background:`${color}08`,padding:'14px 16px',borderBottom:`1px solid ${color}15` }}>
                  <div style={{ fontWeight:700,fontSize:14,color:theme.ink,marginBottom:4 }}>
                    Vous souhaitez participer ?
                  </div>
                  <div style={{ fontSize:13,color:theme.muted,lineHeight:1.55 }}>
                    Connectez-vous à votre espace fidèle pour vous inscrire, consulter les participants et marquer votre présence le jour J.
                  </div>
                </div>
                {/* Boutons — tous deux vers /fidele */}
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  <button onClick={()=>{onClose();navigate('/fidele?action=login');}}
                    style={{ width:'100%', padding:'13px', background:`linear-gradient(135deg,${color},${darken(color,0.25)})`, border:'none', cursor:'pointer', fontWeight:700, fontSize:14, color:'white', display:'flex', alignItems:'center', justifyContent:'center', gap:8, borderRadius:12, boxShadow:`0 4px 14px ${color}40` }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                    J'ai déjà un compte — Me connecter
                  </button>
                  <button onClick={()=>{onClose();navigate('/fidele?action=create');}}
                    style={{ width:'100%', padding:'12px', background:'white', border:`1.5px solid ${color}30`, cursor:'pointer', fontWeight:700, fontSize:14, color:color, display:'flex', alignItems:'center', justifyContent:'center', gap:8, borderRadius:12, transition:'all .15s' }}
                    onMouseEnter={e=>e.currentTarget.style.background=`${color}06`}
                    onMouseLeave={e=>e.currentTarget.style.background='white'}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
                    Créer un compte fidèle
                  </button>
                </div>
              </div>
            )}

            <button onClick={onClose} style={{ width:'100%',padding:'12px',background:'#f8fafc',border:'1px solid #e5e7eb',borderRadius:12,cursor:'pointer',fontWeight:600,fontSize:13,color:theme.muted }}>
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   PAGE PRINCIPALE
══════════════════════════════════════════════ */

/* ── Sub-components (extracted to avoid hooks-in-callbacks) ── */
// ── Composants cartes extraits pour respecter les règles des Hooks ──
function FeatCard({ it, theme }) {
  const [h,setH] = useState(false);
  const Icon = FEAT_ICON_MAP[it.icon] || IconUsers;
  return (
    <div onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)} style={{ display:'flex', gap:18, padding:26, borderRadius:22, background:'#fff', border:`1px solid ${h?theme.primary+'55':theme.line}`, boxShadow:h?'0 16px 34px -16px rgba(15,23,42,.20)':'0 2px 8px rgba(15,23,42,.04)', transform:h?'translateY(-3px)':'none', transition:'transform .22s, box-shadow .22s, border-color .22s' }}>
      <span style={{ flex:'0 0 auto', display:'grid', placeItems:'center', width:54, height:54, borderRadius:15, background:theme.primarySoft, color:theme.primaryStrong, border:`1px solid ${theme.primary}22` }}>
        <Icon size={26} stroke={1.8} color={theme.primaryStrong}/>
      </span>
      <div>
        <h3 style={{ fontSize:19, fontWeight:700, color:theme.ink, margin:0, letterSpacing:'-0.02em' }}>{it.title}</h3>
        <p style={{ fontSize:15, lineHeight:1.6, color:theme.body, margin:'8px 0 0' }}>{it.text||it.desc}</p>
      </div>
    </div>
  );
}

function DynActCard({ act, theme, mobile, onSelect }) {
  const [h,setH] = useState(false);
  const color = act.activity_type_color || theme.primary;
  const labels = { planned:'Planifiée', ongoing:'En cours', completed:'Terminée' };
  const fmtDay = d=>{try{return new Date(d+'T00:00:00').toLocaleDateString('fr-FR',{day:'numeric'});}catch{return '';}};
  const fmtMonth = d=>{try{return new Date(d+'T00:00:00').toLocaleDateString('fr-FR',{month:'short'}).replace('.','').toUpperCase();}catch{return '';}};
  const fmtWeekday = d=>{try{return new Date(d+'T00:00:00').toLocaleDateString('fr-FR',{weekday:'long'});}catch{return '';}};
  return (
    <div onClick={()=>onSelect(act)} onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)}
      style={{ flex:mobile?'0 0 270px':'1 1 300px', maxWidth: mobile?undefined:340, scrollSnapAlign:mobile?'start':undefined, background:'#fff', border:`1px solid ${h?color+'40':theme.line}`, borderRadius:18, overflow:'hidden', cursor:'pointer', boxShadow:h?'0 16px 36px -16px rgba(15,23,42,.20)':'0 1px 3px rgba(15,23,42,.04)', transform:h?'translateY(-3px)':'none', transition:'transform .2s, box-shadow .2s, border-color .2s', display:'flex', flexDirection:'column' }}>
      <div style={{ padding:'18px 18px 0' }}>
        <div style={{ display:'flex', alignItems:'flex-start', gap:14 }}>
          {/* Pavé date */}
          <div style={{ flexShrink:0, width:54, textAlign:'center', borderRadius:12, overflow:'hidden', border:`1px solid ${theme.line}` }}>
            <div style={{ background:color, color:'#fff', fontSize:10.5, fontWeight:800, letterSpacing:'0.06em', padding:'4px 0' }}>{fmtMonth(act.date)}</div>
            <div style={{ fontSize:20, fontWeight:800, color:theme.ink, padding:'5px 0', lineHeight:1 }}>{fmtDay(act.date)}</div>
          </div>
          <div style={{ flex:1, minWidth:0, paddingTop:1 }}>
            <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11, fontWeight:700, color, padding:'3px 9px', borderRadius:999, background:color+'15', marginBottom:7 }}>
              {act.status==='ongoing'&&<span style={{ width:5,height:5,borderRadius:'50%',background:color }}/>}
              {labels[act.status]}{act.activity_type&&<> · {act.activity_type}</>}
            </span>
            <div style={{ fontSize:16, fontWeight:700, color:theme.ink, lineHeight:1.3 }}>{act.name}</div>
          </div>
        </div>
      </div>
      <div style={{ padding:'14px 18px 16px', marginTop:'auto' }}>
        <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:14 }}>
          <div style={{ display:'flex', alignItems:'center', gap:7, fontSize:13, color:theme.body, textTransform:'capitalize' }}>
            <IconCalendar size={13} color={theme.muted} stroke={1.8}/>{fmtWeekday(act.date)}{act.start_time&&<span style={{ color:theme.muted }}> · {act.start_time}</span>}
          </div>
          {act.location&&(
            <div style={{ display:'flex', alignItems:'center', gap:7, fontSize:13, color:theme.body }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={theme.muted} strokeWidth="1.8"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              {act.location}
            </div>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingTop:13, borderTop:`1px solid ${theme.lineSoft}` }}>
          <span style={{ fontSize:13, fontWeight:700, color, display:'flex', alignItems:'center', gap:4 }}>Voir les détails<IconArrow size={13} stroke={2.2}/></span>
        </div>
      </div>
    </div>
  );
}

function StaticActCard({ it, theme, mobile }) {
  const [h,setH] = useState(false);
  const cc = CAT[it.cat];
  const Icon = ACT_ICON_MAP[it.icon] || IconCalendar;
  return (
    <div onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)} style={{ flex:mobile?'0 0 270px':undefined, scrollSnapAlign:mobile?'start':undefined, background:'#fff', border:`1px solid ${theme.line}`, borderRadius:22, overflow:'hidden', boxShadow:h?'0 18px 38px -18px rgba(15,23,42,.24)':'0 2px 10px rgba(15,23,42,.05)', transform:h?'translateY(-4px)':'none', transition:'transform .22s, box-shadow .22s' }}>
      <div style={{ position:'relative', height:116, background:`linear-gradient(135deg,${cc.a},${cc.b})`, padding:18, display:'flex', flexDirection:'column', justifyContent:'space-between', overflow:'hidden' }}>
        <div style={{ position:'absolute', right:-24, bottom:-28, width:120, height:120, borderRadius:'50%', background:'rgba(255,255,255,.12)' }}/>
        <span style={{ position:'relative', display:'grid', placeItems:'center', width:44, height:44, borderRadius:13, background:'rgba(255,255,255,.22)', color:'#fff' }}><Icon size={23} stroke={1.9} color="#fff"/></span>
        <span style={{ position:'relative', alignSelf:'flex-start', fontSize:11.5, fontWeight:700, color:'#fff', padding:'4px 11px', borderRadius:999, background:'rgba(255,255,255,.20)' }}>{it.meta}</span>
      </div>
      <div style={{ padding:'18px 20px 20px' }}>
        <h3 style={{ fontSize:18.5, fontWeight:700, color:theme.ink, margin:0, letterSpacing:'-0.02em' }}>{it.name}</h3>
        <p style={{ fontSize:14.5, lineHeight:1.55, color:theme.body, margin:'8px 0 16px' }}>{it.desc}</p>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingTop:14, borderTop:`1px solid ${theme.lineSoft}` }}>
          <span style={{ display:'inline-flex', alignItems:'center', gap:7, fontSize:13.5, fontWeight:600, color:theme.muted }}><IconUsers size={17} color={cc.b} stroke={1.8}/>{it.count} inscrits</span>
          <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:13.5, fontWeight:700, color:cc.b }}>Détails<IconArrow size={15} stroke={2.2}/></span>
        </div>
      </div>
    </div>
  );
}

/* ── Icônes réseaux sociaux ── */
const PLATFORM_ICONS = {
  youtube:   { icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="#FF0000"><path d="M23 7s-.3-2-1.2-2.7C20.7 3.2 19.5 3.2 19 3.1 16 3 12 3 12 3S8 3 5 3.1c-.5.1-1.7.1-2.8 1.2C1.3 5 1 7 1 7S.7 9.1.7 11.2v2c0 2 .3 4.1.3 4.1s.3 2 1.2 2.7c1.1 1.1 2.6 1 3.3 1.1C7.2 21.2 12 21.2 12 21.2s4 0 7-.2c.5-.1 1.7-.1 2.8-1.2.9-.7 1.2-2.7 1.2-2.7s.3-2.1.3-4.1v-2C23.3 9.1 23 7 23 7zM9.7 15.5V8.4l7.6 3.6-7.6 3.5z"/></svg>, label:'YouTube', color:'#FF0000', bg:'#fff0f0' },
  facebook:  { icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12c0-6.6-5.4-12-12-12S0 5.4 0 12c0 6 4.4 11 10.1 11.9V15.5H7.1V12h3V9.3c0-3 1.8-4.7 4.5-4.7 1.3 0 2.7.2 2.7.2v3H15.8c-1.5 0-2 .9-2 1.9V12h3.3l-.5 3.5h-2.8v8.4C19.6 23 24 18 24 12z"/></svg>, label:'Facebook', color:'#1877F2', bg:'#eff5ff' },
  tiktok:    { icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="#000"><path d="M19.6 6.7a4.8 4.8 0 0 1-3.8-2.7V17a5 5 0 1 1-4-4.9V10a8.3 8.3 0 0 0-1-.1 8 8 0 1 0 8 8V6.7z"/></svg>, label:'TikTok', color:'#000', bg:'#f4f4f4' },
  instagram: { icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="url(#ig)" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.5" fill="#E1306C"/><defs><linearGradient id="ig" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="#f09433"/><stop offset="100%" stopColor="#bc1888"/></linearGradient></defs></svg>, label:'Instagram', color:'#E1306C', bg:'#fff0f5' },
  link:      { icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>, label:'Voir', color:'#64748b', bg:'#f8fafc' },
};

/* ── Modale détail affiche ── */
function NewsModal({ item, theme, onClose }) {
  const platform = PLATFORM_ICONS[item.link_type || 'link'] || PLATFORM_ICONS.link;
  const isVideo = item.image_url && (item.image_url.endsWith('.mp4') || item.image_url.endsWith('.webm') || item.image_url.endsWith('.mov'));

  return (
    <div
      onClick={onClose}
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', padding:20, backdropFilter:'blur(12px)' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background:'#fff', borderRadius:24, width:'100%', maxWidth:540, boxShadow:'0 40px 100px rgba(0,0,0,0.4)', overflow:'hidden', maxHeight:'90vh', display:'flex', flexDirection:'column' }}
      >
        {/* Image / Vidéo */}
        {item.image_url && (
          <div style={{ position:'relative', background:'#0f172a', flexShrink:0 }}>
            {isVideo ? (
              <video src={item.image_url} controls autoPlay style={{ width:'100%', maxHeight:320, display:'block', objectFit:'contain' }}/>
            ) : (
              <img src={item.image_url} alt={item.title} style={{ width:'100%', maxHeight:360, objectFit:'cover', display:'block' }}/>
            )}
            {/* Bouton fermer */}
            <button onClick={onClose} style={{ position:'absolute', top:12, right:12, width:36, height:36, borderRadius:'50%', background:'rgba(0,0,0,0.55)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(6px)' }}>
              <IconClose size={18} color="white"/>
            </button>
            {/* Badge en haut à gauche */}
            {item.badge && (
              <div style={{ position:'absolute', top:12, left:12, background:theme.primary, borderRadius:8, padding:'4px 12px' }}>
                <span style={{ fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.06em' }}>{item.badge}</span>
              </div>
            )}
          </div>
        )}

        {/* Corps */}
        <div style={{ padding:'24px 26px 26px', overflowY:'auto' }}>
          {!item.image_url && (
            <button onClick={onClose} style={{ float:'right', background:'#f1f5f9', border:'none', borderRadius:'50%', width:32, height:32, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <IconClose size={16} color="#64748b"/>
            </button>
          )}

          {/* Badge si pas d'image */}
          {item.badge && !item.image_url && (
            <div style={{ display:'inline-block', background:theme.primarySoft, color:theme.primaryStrong, borderRadius:8, padding:'4px 10px', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:12 }}>{item.badge}</div>
          )}

          <h2 style={{ fontSize:22, fontWeight:800, color:theme.ink, margin:'0 0 10px', lineHeight:1.25, letterSpacing:'-0.02em' }}>{item.title}</h2>

          {item.desc && (
            <p style={{ fontSize:15.5, color:theme.body, lineHeight:1.75, margin:'0 0 22px' }}>{item.desc}</p>
          )}

          {/* Bouton principal */}
          {item.link_url && (
            <a href={item.link_url} target="_blank" rel="noopener noreferrer"
              style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10, width:'100%', padding:'14px', background:`linear-gradient(135deg,${theme.primary},${darken(theme.primary,0.2)})`, color:'white', borderRadius:14, textDecoration:'none', fontWeight:700, fontSize:16, boxShadow:`0 6px 20px ${theme.primary}40`, marginBottom:10 }}>
              {platform.icon}
              {item.link_type === 'youtube' ? 'Regarder sur YouTube' :
               item.link_type === 'facebook' ? 'Voir sur Facebook' :
               item.link_type === 'tiktok' ? 'Voir sur TikTok' :
               item.link_type === 'instagram' ? 'Voir sur Instagram' : 'Ouvrir le lien'}
              <IconArrow size={16} color="white"/>
            </a>
          )}

          {/* Liens supplémentaires nommés (Facebook, Instagram...) */}
          {item.links?.length > 0 && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:10 }}>
              {item.links.map((l, i) => (
                <a key={i} href={l.url} target="_blank" rel="noopener noreferrer"
                  style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'9px 16px', background:theme.app, border:`1px solid ${theme.line}`, color:theme.ink, borderRadius:10, textDecoration:'none', fontWeight:600, fontSize:13.5 }}>
                  {l.label || 'Voir le lien'}
                  <IconArrow size={13} color={theme.ink}/>
                </a>
              ))}
            </div>
          )}

          <button onClick={onClose} style={{ width:'100%', padding:'12px', background:'#f8fafc', border:'1px solid #e5e7eb', borderRadius:12, cursor:'pointer', fontWeight:600, fontSize:14, color:'#64748b' }}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

function NewsCard({ item, theme, onOpenModal }) {
  const [h, setH] = useState(false);
  const platform = PLATFORM_ICONS[item.link_type || 'link'] || PLATFORM_ICONS.link;
  const hasImage = !!item.image_url;

  return (
    <div
      onClick={() => onOpenModal(item)}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{ borderRadius: 20, overflow: 'hidden', cursor: 'pointer', transition: 'all .22s', transform: h ? 'translateY(-6px)' : 'none', boxShadow: h ? '0 24px 56px rgba(15,23,42,.18)' : '0 4px 18px rgba(15,23,42,.08)', position: 'relative' }}
    >
      {/* Zone image — style affiche */}
      {hasImage ? (
        <div className="cm-news-card-img" style={{ position: 'relative', aspectRatio: '3/4', overflow: 'hidden', background: '#111' }}>
          <img src={item.image_url} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform .4s', transform: h ? 'scale(1.04)' : 'scale(1)' }}/>
          {/* Overlay dégradé bas */}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)' }}/>
          {/* Badge plateforme en haut à droite */}
          {item.link_url && (
            <div style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)', borderRadius: 10, padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 5 }}>
              {platform.icon}
              <span style={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>{platform.label}</span>
            </div>
          )}
          {/* Badge type en haut à gauche */}
          {item.badge && (
            <div style={{ position: 'absolute', top: 12, left: 12, background: theme.primary, borderRadius: 8, padding: '4px 10px' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{item.badge}</span>
            </div>
          )}
          {/* Titre + desc + bouton en overlay bas */}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '20px 18px 18px' }}>
            <div style={{ fontWeight: 800, fontSize: 17, color: '#fff', lineHeight: 1.25, marginBottom: item.desc ? 6 : 10 }}>{item.title}</div>
            {item.desc && <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: 10 }}>{item.desc}</div>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)', borderRadius: 8, padding: '7px 13px', fontSize: 12.5, fontWeight: 700, color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                Voir les détails
              </div>
              {item.link_url && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', borderRadius: 8, padding: '7px 12px', fontSize: 12.5, fontWeight: 700, color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}>
                  {platform.icon} {platform.label}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Sans image — carte texte sobre */
        <div style={{ background: '#fff', border: `1.5px solid ${h ? theme.primary + '50' : theme.line}`, borderRadius: 20, padding: '24px 22px', minHeight: 180, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              {item.badge && <div style={{ background: theme.primarySoft, color: theme.primaryStrong, borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{item.badge}</div>}
              {item.link_url && <div style={{ background: platform.bg, borderRadius: 8, padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>{platform.icon}</div>}
            </div>
            <div style={{ fontWeight: 800, fontSize: 18, color: theme.ink, lineHeight: 1.25, marginBottom: 8 }}>{item.title}</div>
            {item.desc && <div style={{ fontSize: 14, color: theme.body, lineHeight: 1.65 }}>{item.desc}</div>}
          </div>
          {item.link_url && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 16, color: platform.color || theme.primaryStrong, fontSize: 13.5, fontWeight: 700 }}>
              {platform.icon} {platform.label !== 'Voir' ? `Voir sur ${platform.label}` : 'Ouvrir le lien'} <IconArrow size={13}/>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatCell({ s, seen, theme, divider }) {
  const v = useCountUp(typeof s.value==='number'?s.value:parseInt(s.value)||0, seen);
  const display = (typeof s.value==='number'?s.value:parseInt(s.value)||0)>=100?Math.round(v):v.toFixed(0);
  return (
    <div className='cm-stat-cell' style={{ textAlign:'center', padding:'8px 14px', borderLeft:(divider)?`1px solid ${theme.line}`:'none' }}>
      <div className='cm-stat-val' style={{ fontSize:42, fontWeight:800, letterSpacing:'-0.03em', color:theme.ink, lineHeight:1 }}>{seen?display:0}<span style={{ color:theme.primary }}>{s.suffix}</span></div>
      <div style={{ fontSize:13, fontWeight:600, color:theme.muted, marginTop:9 }}>{s.label}</div>
    </div>
  );
}

export default function LandingPage() {
  const { user } = useAuth();
  const { config } = useSiteConfig();
  const navigate = useNavigate();
  const mobile = useMediaQuery('(max-width: 860px)');
  const theme = makeTheme(config.primaryColor || '#000000');

  const [modalDest, setModalDest]     = useState(null);
  const [contactType, setContactType] = useState(null);
  const [selectedAct, setSelectedAct] = useState(null);
  const [selectedNews, setSelectedNews] = useState(null);
  const [landing, setLanding]         = useState(null);
  const [realStats, setRealStats]     = useState(null);
  const [activities, setActivities]   = useState([]);
  const [gallery, setGallery]         = useState([]);
  const [families, setFamilies]       = useState([]);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [galleryFilter, setGalleryFilter] = useState('all');

  const loadLanding = useCallback(()=>{ axios.get(`${API}/notifications/church-settings/`).then(r=>setLanding(r.data)).catch(()=>{}); },[]);

  useEffect(()=>{
    axios.get(`${API}/notifications/public-stats/`).then(r=>setRealStats(r.data)).catch(()=>{});
  },[]);
  useEffect(()=>{ loadLanding(); },[loadLanding]);
  useEffect(()=>{ axios.get(`${API}/activities/public/`).then(r=>setActivities(Array.isArray(r.data)?r.data:[])).catch(()=>{}); },[]);
  useEffect(()=>{
    axios.get(`${API}/activities/gallery/`).then(r=>{
      const items = Array.isArray(r.data) ? r.data : [];
      // Ne garder que les médias réellement affichables (image/vidéo avec fichier,
      // live avec URL ou fichier) pour éviter les cartes vides dans le carrousel.
      setGallery(items.filter(m =>
        m.media_type === 'live' ? !!(m.file_url || m.external_url) : !!m.file_url
      ));
    }).catch(()=>{});
  },[]);
  useEffect(()=>{
    if (landing?.use_families) {
      axios.get(`${API}/participants/public/families/`).then(r=>setFamilies(Array.isArray(r.data)?r.data:[])).catch(()=>{});
    }
  },[landing?.use_families]);

  const cfg = {
    brand: landing?.landing_brand_name || config.siteName || 'RCC',
    logoUrl: landing?.landing_brand_logo || config.logoUrl || '',
    heroBg: {
      from:    landing?.landing_bg_from || lighten(theme.primary,0.88),
      to:      landing?.landing_bg_to   || lighten(theme.gold,0.85),
      image:   landing?.landing_bg_image || '',
      overlayColor:   landing?.landing_overlay_color || theme.surface,
      overlayOpacity: landing?.landing_overlay_opacity ?? 0.6,
      imageOpacity:   landing?.landing_image_opacity ?? 0.16,
      titleColor:    landing?.landing_title_color || '',
      subtitleColor: landing?.landing_subtitle_color || '',
    },
    hero: {
      badge:    landing?.landing_hero_badge   || 'Plateforme multi-églises',
      titleA:   (landing?.landing_title||'Toute votre église,').split(',')[0] + (landing?.landing_title?.includes(',')?',':''),
      titleB:   landing?.landing_title?.includes(',') ? landing.landing_title.split(',').slice(1).join(',').trim() : 'réunie dans une appli.',
      subtitle: landing?.landing_subtitle     || "RCC centralise vos fidèles, leurs présences et vos activités. Du scan QR à l'entrée jusqu'au tableau de bord du pasteur.",
      cta1:     landing?.landing_cta_primary  || 'Démarrer maintenant',
      cta2:     landing?.landing_cta_secondary|| 'Espace fidèle',
      note:     'Sans engagement · Mise en place en quelques minutes',
    },
    stats: (landing?.landing_stats?.length>0) ? landing.landing_stats : [
      { value: realStats?.total_fideles   ?? 0, suffix:'', label:'Fidèles suivis' },
      { value: realStats?.total_presences ?? 0, suffix:'', label:'Présences enregistrées' },
      { value: realStats?.total_eglises   ?? 0, suffix:'', label:'Églises connectées' },
      { value: realStats?.total_activites ?? 0, suffix:'', label:'Activités organisées' },
    ],
    features: {
      eyebrow: 'Fonctionnalités',
      title: 'Une plateforme, trois espaces, zéro feuille de présence',
      subtitle: "Fidèle, administration et accueil à l'entrée — chacun son espace, tout est connecté.",
      items: (landing?.landing_features?.length>0) ? landing.landing_features.map(f=>({...f, icon:f.icon==='users'?'IconUsers':f.icon==='check'?'IconCheck':f.icon==='bar'?'IconChart':f.icon==='qr'?'IconQr':f.icon==='cal'?'IconCalendar':f.icon==='bell'?'IconBell':'IconUsers'})) : [
        { icon:'IconUsers',    title:'Annuaire des fidèles',      text:'Fiches complètes, familles et groupes, historique de présence. Une recherche instantanée.' },
        { icon:'IconQr',       title:'Présence par QR',           text:"Un scan à l'entrée et la présence est enregistrée en temps réel, sans file d'attente." },
        { icon:'IconCalendar', title:'Activités & événements',    text:'Cultes, réunions, formations. Inscriptions, rappels et suivi des participants.' },
        { icon:'IconChart',    title:'Tableau de bord pasteur',   text:'Assiduité, croissance et tendances par groupe. Des décisions guidées par des chiffres.' },
      ],
    },
    activites: {
      eyebrow: 'Vie de l\'église',
      title: landing?.landing_activities_title || 'Animez chaque rendez-vous de la communauté',
      subtitle: 'Créez vos activités, ouvrez les inscriptions et suivez la présence en temps réel.',
      staticItems: [
        { name:'Culte du dimanche',  meta:'Chaque dimanche · 09h00', cat:'blue',   icon:'IconHands',  count:320, desc:'Le grand rassemblement hebdomadaire, présence enregistrée au scan dès l\'entrée.' },
        { name:'École du dimanche',  meta:'Enfants · 08h30',          cat:'green',  icon:'IconBook',   count:64,  desc:'Suivi des enfants par classe, avec émargement des moniteurs et des parents.' },
        { name:'Groupe de louange',  meta:'Répétitions · vendredi',  cat:'purple', icon:'IconMusic',  count:28,  desc:'Planning des répétitions, disponibilités et feuille de service de l\'équipe.' },
        { name:'Cellule de prière',  meta:'Mardi · 19h00',            cat:'amber',  icon:'IconHeart',  count:45,  desc:'Petits groupes de maison, présence remontée automatiquement au responsable.' },
        { name:'Étude biblique',     meta:'Mercredi · 18h30',         cat:'cyan',   icon:'IconBook',   count:86,  desc:'Sessions thématiques avec inscriptions, supports partagés et émargement.' },
        { name:'Action sociale',     meta:'Un samedi / mois',          cat:'green',  icon:'IconHands',  count:52,  desc:'Mobilisation des bénévoles, listes de présence et suivi des actions menées.' },
      ],
    },
    temoignages: {
      eyebrow: 'Témoignages', title: 'Adoptée par les pasteurs et les fidèles',
      items: (landing?.landing_testimonials?.length>0) ? landing.landing_testimonials : [
        { quote:"Avant, je comptais les présences à la main. Aujourd'hui le tableau de bord me dit tout, par groupe et par dimanche.", name:'Pasteur Emmanuel K.', role:'RCC · Abidjan', stars:5 },
        { quote:"Le scan QR à l'entrée a transformé nos dimanches. Plus de file d'attente, plus de feuilles perdues.", name:'Grâce A.', role:'Responsable accueil', stars:5 },
        { quote:"Je vois mes activités, je m'inscris et je retrouve ma carte de membre directement dans l'appli.", name:'Jonathan T.', role:'Membre de la communauté', stars:5 },
      ],
    },
    cta: {
      title: 'Prêt à rapprocher votre communauté ?',
      subtitle: 'Rejoignez les églises qui digitalisent leur suivi avec RCC. Créez votre espace en quelques minutes.',
      cta1: 'Créer mon espace église', cta2: 'Parler à l\'équipe',
    },
    footer: {
      tagline: 'La plateforme qui rassemble vos fidèles, vos présences et vos activités.',
      columns: [
        { title:'Produit', links:['Fonctionnalités','Espace fidèle','Administration','Accueil entrée'] },
        { title:'Église', links:['Activités','Présences','Multi-églises','Tarifs'] },
        { title:'Ressources', links:['Guide de démarrage','Support','Témoignages','Contact'] },
      ],
      legal: `© ${new Date().getFullYear()} ${landing?.landing_brand_name || config.siteName || 'RCC'} · Tous droits réservés`,
    },
    news: landing?.landing_news || [],
    about: {
      title: landing?.landing_about_title || 'Qui sommes-nous',
      text:  landing?.landing_about_text  || '',
      image: landing?.landing_about_image || '',
    },
    vision: {
      title: landing?.landing_vision_title || 'Notre vision',
      text:  landing?.landing_vision_text  || '',
    },
    departments: landing?.landing_departments || [],
    programs:    landing?.landing_programs    || [],
    live: {
      enabled: landing?.landing_live_enabled || false,
      url:     landing?.landing_live_url     || '',
      title:   landing?.landing_live_title   || 'Diffusion en direct',
    },
  };

  const showActivities = landing?.landing_show_activities !== false;

  const hasLiveActs = activities.some(a=>a.status!=='completed');

  // Stats counter
  const [statsRef, statsSeen] = useInView({ threshold:0.4 });

  return (
    <div style={{ fontFamily:"system-ui,-apple-system,'Segoe UI',Roboto,sans-serif", background:theme.surface, color:theme.body, overflowX:'hidden' }}>
      <style>{`
        @keyframes cmFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-16px)} }
        @keyframes cmFadeUp { from{opacity:0; transform:translateY(28px)} to{opacity:1; transform:translateY(0)} }
        @keyframes cmFadeIn { from{opacity:0} to{opacity:1} }
        @keyframes cmBgPan { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
        .cm-fade-up { opacity:0; animation:cmFadeUp .8s ease-out forwards; }
        .cm-fade-in { opacity:0; animation:cmFadeIn 1s ease-out forwards; }
        .cm-hero-bg-image { animation:cmFadeIn 1.2s ease-out forwards, cmBgPan 30s ease-in-out infinite; background-size:130% auto; }
        .cm-reveal { opacity:0; transform:translateY(24px); transition:opacity .7s ease-out, transform .7s ease-out; }
        .cm-reveal.cm-in { opacity:1; transform:translateY(0); }
        .cm-scroll::-webkit-scrollbar{height:6px}
        .cm-scroll::-webkit-scrollbar-thumb{background:rgba(15,23,42,.18);border-radius:99px}
        .cm-scroll::-webkit-scrollbar-track{background:transparent}
        .cm-news-scroll::-webkit-scrollbar { display: none; }

        /* ════ DESKTOP — affiches en grille ════ */
        @media (min-width: 861px) {
          .cm-news-scroll { display: grid !important; grid-template-columns: repeat(3, 1fr); gap: 18px; overflow-x: visible !important; margin: 0 !important; padding: 0 !important; scroll-snap-type: none !important; }
          .cm-news-item { min-width: unset !important; max-width: unset !important; flex-shrink: unset !important; }
        }

        /* ════ MOBILE — vraies media queries CSS ════ */
        @media (max-width: 860px) {
          /* Hero */
          .cm-hero-phone { display: none !important; }
          .cm-hero-pad { padding-top: 90px !important; padding-bottom: 40px !important; }
          .cm-hero-inner { flex-direction: column !important; gap: 28px !important; }
          .cm-hero-text { max-width: 100% !important; text-align: center !important; }
          .cm-hero-h1 { font-size: 36px !important; }
          .cm-hero-sub { font-size: 16px !important; }
          .cm-hero-btns { justify-content: center !important; }
          .cm-hero-note { justify-content: center !important; }

          /* Stats */
          .cm-stats-wrap { transform: none !important; }
          .cm-stats-grid { grid-template-columns: 1fr 1fr !important; gap: 10px !important; padding: 16px !important; border-radius: 16px !important; }
          .cm-stat-cell { border-left: none !important; border-top: 1px solid #e5e9f2; padding: 10px 8px !important; }
          .cm-stat-cell:nth-child(-n+2) { border-top: none !important; }
          .cm-stat-val { font-size: 32px !important; }

          /* Sections */
          .cm-section-feat { padding: 40px 0 !important; }
          .cm-feat-grid { grid-template-columns: 1fr !important; gap: 12px !important; margin-top: 28px !important; }
          .cm-feat-card { padding: 18px !important; }
          .cm-feat-icon { width: 44px !important; height: 44px !important; }

          /* Activités */
          .cm-acts-section { padding: 40px 0 !important; }

          /* Affiches — mobile : carrousel */
          .cm-news-section { padding: 40px 0 !important; }
          .cm-news-header { flex-direction: column !important; align-items: flex-start !important; }
          .cm-news-scroll { margin: 0 -24px !important; }
          .cm-news-item { min-width: 240px !important; max-width: 240px !important; }
          .cm-news-card-img { aspect-ratio: 3/4 !important; }

          /* Témoignages */
          .cm-testi-section { padding: 48px 0 !important; }
          .cm-testi-grid { grid-template-columns: 1fr !important; gap: 14px !important; margin-top: 32px !important; }

          /* CTA */
          .cm-cta-section { padding: 40px 0 !important; }
          .cm-cta-box { padding: 36px 20px !important; border-radius: 20px !important; }
          .cm-cta-h2 { font-size: 28px !important; }

          /* Footer */
          .cm-footer { padding: 36px 0 24px !important; }
          .cm-footer-grid { grid-template-columns: 1fr !important; gap: 0 !important; }
          .cm-footer-cols { display: none !important; }
        }
      `}</style>

      <Navbar config={cfg} theme={theme} mobile={mobile}
        onLogin={()=>setModalDest('admin')} onFidele={()=>navigate('/fidele')}
        user={user} navigate={navigate} hasEvents={cfg.news.length>0} hasGallery={gallery.length>0}/>

      {/* ══ HERO ══ */}
      <section id="top" style={{ position:'relative', overflow:'hidden', background:`linear-gradient(135deg, ${cfg.heroBg.from} 0%, ${theme.surface} 45%, ${cfg.heroBg.to} 100%)` }}>
        {cfg.heroBg.image && (
          <>
            <div className="cm-hero-bg-image" style={{ position:'absolute', inset:0, backgroundImage:`url(${cfg.heroBg.image})`, backgroundSize:'cover', backgroundPosition:'center', opacity:cfg.heroBg.imageOpacity }}/>
            <div style={{ position:'absolute', inset:0, background:hexToRgba(cfg.heroBg.overlayColor, cfg.heroBg.overlayOpacity) }}/>
          </>
        )}
        <div style={{ position:'absolute', inset:0, background:`radial-gradient(120% 80% at 80% -10%, ${theme.primarySoft} 0%, transparent 55%), radial-gradient(90% 60% at 0% 0%, ${lighten(theme.primary,0.8)} 0%, transparent 50%)` }}/>
        <div style={{ position:'absolute', inset:0, backgroundImage:`linear-gradient(${theme.line} 1px,transparent 1px),linear-gradient(90deg,${theme.line} 1px,transparent 1px)`, backgroundSize:'46px 46px', opacity:.35, maskImage:'radial-gradient(80% 60% at 50% 30%,#000,transparent 75%)', WebkitMaskImage:'radial-gradient(80% 60% at 50% 30%,#000,transparent 75%)' }}/>
        <Container className="cm-hero-pad" style={{ position:'relative', display:'flex', flexDirection:'row', alignItems:'center', gap:48, paddingTop:150, paddingBottom:110 }}>
          <div className='cm-hero-text cm-fade-up' style={{ flex:'1 1 0', maxWidth:560, textAlign:'left' }}>
            {cfg.hero.badge && (
              <div style={{ display:'inline-flex', alignItems:'center', gap:9, padding:'7px 14px 7px 8px', borderRadius:999, background:'#fff', border:`1px solid ${theme.line}`, boxShadow:'0 2px 8px rgba(15,23,42,.05)', fontSize:13, fontWeight:600, color:theme.body, marginBottom:24 }}>
                <span style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'3px 9px', borderRadius:999, background:theme.primarySoft, color:theme.primaryStrong, fontWeight:700, fontSize:12 }}>
                  <IconGlobe size={14} stroke={2}/>{cfg.hero.badge}
                </span>
              </div>
            )}
            <h1 className='cm-hero-h1' style={{ fontSize:60, lineHeight:1.04, fontWeight:800, letterSpacing:'-0.035em', color:cfg.heroBg.titleColor || theme.ink, margin:0 }}>
              {cfg.hero.titleA}<br/>
              <span style={{ background:`linear-gradient(120deg,${theme.primary},${theme.primaryStrong})`, WebkitBackgroundClip:'text', backgroundClip:'text', color:'transparent' }}>{cfg.hero.titleB}</span>
            </h1>
            <p className='cm-hero-sub' style={{ fontSize:19, lineHeight:1.6, color:cfg.heroBg.subtitleColor || theme.body, margin:'22px 0 0', maxWidth:520 }}>{cfg.hero.subtitle}</p>
            {landing?.landing_verse && (
              <div style={{ margin:'20px 0 0', padding:'12px 16px', background:`${theme.primarySoft}`, borderLeft:`3px solid ${theme.primary}`, borderRadius:'0 10px 10px 0', maxWidth:480, marginLeft:mobile?'auto':0, marginRight:mobile?'auto':0 }}>
                <p style={{ fontStyle:'italic', color:theme.body, fontSize:15, margin:'0 0 6px', lineHeight:1.7 }}>« {landing.landing_verse} »</p>
                {landing.landing_verse_ref&&<p style={{ color:theme.primaryStrong, fontWeight:700, fontSize:13, margin:0 }}>— {landing.landing_verse_ref}</p>}
              </div>
            )}
            <div className='cm-hero-btns' style={{ display:'flex', gap:13, marginTop:32, flexWrap:'wrap', justifyContent:'flex-start' }}>
              {/* RCC-UX-001 : l'Espace fidèle est le bouton principal, l'accès admin passe en second */}
              <Btn theme={theme} variant="primary" size="lg" icon onClick={()=>navigate('/fidele')}>{cfg.hero.cta2}</Btn>
              <Btn theme={theme} variant="ghost" size="lg" onClick={user?()=>navigate('/dashboard'):()=>setModalDest('admin')}
                style={cfg.heroBg.image ? { color:cfg.heroBg.titleColor || theme.ink, borderColor:hexToRgba(cfg.heroBg.titleColor || theme.ink, 0.35) } : undefined}>
                {cfg.hero.cta1}
              </Btn>
            </div>
            <div className='cm-hero-note' style={{ display:'flex', alignItems:'center', gap:8, marginTop:20, justifyContent:'flex-start', color:theme.muted, fontSize:13.5, fontWeight:500 }}>
              <IconCheck size={16} color={theme.primary} stroke={2.4}/>{cfg.hero.note}
            </div>
            {mobile && gallery.length > 0 && (
              <FanCarousel
                items={gallery}
                style={{ marginTop: 24 }}
                onItemSelect={(i)=>setSelectedMedia(gallery[i])}
              />
            )}
          </div>
          <div className="cm-hero-phone cm-fade-up" style={{ flex:'0 0 auto', display:'grid', placeItems:'center', animationDelay:'.15s' }}>
            <PhoneMockup theme={theme} landingPhone={{ member: landing?.landing_phone_member_name, role: landing?.landing_phone_member_role }}/>
          </div>
        </Container>
      </section>

      {/* ══ QUI SOMMES-NOUS / NOTRE VISION ══ */}
      {(cfg.about.text || cfg.vision.text) && (
        <section id="apropos" style={{ background:theme.surface, padding:'90px 0', borderTop:`1px solid ${theme.line}` }}>
          <Container>
            <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : (cfg.about.image ? '1fr 1fr' : '1fr'), gap:48, alignItems:'center' }}>
              {cfg.about.image && !mobile && (
                <Reveal>
                  <img src={cfg.about.image} alt={cfg.about.title} style={{ width:'100%', borderRadius:24, objectFit:'cover', aspectRatio:'4/3', boxShadow:'0 24px 60px -24px rgba(15,23,42,.25)' }}/>
                </Reveal>
              )}
              <div>
                {cfg.about.text && (
                  <Reveal style={{ marginBottom: cfg.vision.text ? 44 : 0 }}>
                    <Eyebrow theme={theme}>Notre église</Eyebrow>
                    <h2 style={{ fontSize:mobile?28:36, fontWeight:800, letterSpacing:'-0.02em', color:theme.ink, margin:'14px 0 16px', lineHeight:1.15 }}>{cfg.about.title}</h2>
                    <p style={{ fontSize:16.5, lineHeight:1.75, color:theme.body, margin:0, whiteSpace:'pre-line' }}>{cfg.about.text}</p>
                  </Reveal>
                )}
                {cfg.vision.text && (
                  <Reveal style={{ transitionDelay:'.1s' }}>
                    <Eyebrow theme={theme} color={theme.gold}>Notre vision</Eyebrow>
                    <h2 style={{ fontSize:mobile?24:30, fontWeight:800, letterSpacing:'-0.02em', color:theme.ink, margin:'14px 0 16px', lineHeight:1.15 }}>{cfg.vision.title}</h2>
                    <p style={{ fontSize:16, lineHeight:1.75, color:theme.body, margin:0, whiteSpace:'pre-line' }}>{cfg.vision.text}</p>
                  </Reveal>
                )}
              </div>
              {cfg.about.image && mobile && (
                <img src={cfg.about.image} alt={cfg.about.title} style={{ width:'100%', borderRadius:20, objectFit:'cover', aspectRatio:'4/3' }}/>
              )}
            </div>
          </Container>
        </section>
      )}

      {/* ══ ACTUALITÉS ══ */}
      {cfg.news.length > 0 && (
        <section id="evenements" className='cm-news-section' style={{ background: theme.app, padding: '90px 0', borderTop: `1px solid ${theme.line}`, borderBottom: `1px solid ${theme.line}` }}>
          <Container>
            {/* Header section */}
            <div className="cm-news-header" style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', gap:16, marginBottom:44 }}>
              <SectionHead
                eyebrow="Affiches & Événements"
                title="À venir dans votre église"
                subtitle="Retrouvez toutes nos annonces, cultes spéciaux et diffusions en direct."
                theme={theme} mobile={mobile}
              />
            </div>
            {/* Carrousel sur mobile (CSS), grille sur desktop */}
            <div className="cm-news-scroll" style={{ display:'flex', gap:16, overflowX:'auto', scrollSnapType:'x mandatory', margin:'0 -24px', padding:'4px 24px 16px', WebkitOverflowScrolling:'touch' }}>
              {cfg.news.map((item,i) => (
                <div key={i} className="cm-news-item" style={{ minWidth:260, maxWidth:260, scrollSnapAlign:'start', flexShrink:0 }}>
                  <NewsCard item={item} theme={theme} onOpenModal={setSelectedNews}/>
                </div>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* ══ ACTIVITÉS (uniquement depuis la base) ══ */}
      <section id='activites' className='cm-acts-section' style={{ background:theme.app, padding:'90px 0 100px', borderTop:`1px solid ${theme.line}`, borderBottom:`1px solid ${theme.line}` }}>
        <Container>
          <SectionHead
            eyebrow={hasLiveActs ? 'Au programme' : 'Activités récentes'}
            title={cfg.activites.title}
            subtitle={cfg.activites.subtitle}
            theme={theme} mobile={mobile} center={!mobile}
          />
          {activities.length === 0 ? (
            <div style={{ textAlign:'center', padding:'48px 0', color:theme.muted, marginTop:32 }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={theme.line} strokeWidth="1.5" style={{ display:'block', margin:'0 auto 12px' }}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              Aucune activité planifiée pour le moment.
            </div>
          ) : (
            <div style={{ marginTop:36 }}>
              {/* Desktop : grille 3 col — Mobile : carrousel */}
              <div style={{ display:mobile?'flex':'flex', flexWrap: mobile?'nowrap':'wrap', justifyContent: mobile?'flex-start':'center', gap:18, overflowX:mobile?'auto':'visible', scrollSnapType:mobile?'x mandatory':undefined, margin:mobile?'0 -24px':'0', padding:mobile?'4px 24px 14px':'0', WebkitOverflowScrolling:'touch' }} className={mobile?'cm-scroll':''}>
                {activities.slice(0,6).map(act => (
                  <DynActCard key={act.id} act={act} theme={theme} mobile={mobile} onSelect={setSelectedAct}/>
                ))}
              </div>
              {activities.length > 6 && (
                <div style={{ textAlign:'center', marginTop:28 }}>
                  <span style={{ fontSize:13, color:theme.muted, fontWeight:500 }}>
                    + {activities.length - 6} autres activités — connectez-vous pour tout voir
                  </span>
                </div>
              )}
            </div>
          )}
        </Container>
      </section>

      {/* ══ DÉPARTEMENTS ══ */}
      {cfg.departments.length > 0 && (
        <section id='departements' style={{ background:theme.surface, padding:'90px 0 100px' }}>
          <Container>
            <SectionHead eyebrow="Vie communautaire" title="Nos départements" subtitle="Chaque service de l'église, porté par des équipes dédiées." theme={theme} mobile={mobile} center={!mobile}/>
            <div style={{ display:'flex', flexWrap:'wrap', justifyContent:'center', gap:18, marginTop:44 }}>
              {cfg.departments.map((d,i) => (
                <Reveal key={i} style={{ transitionDelay:`${i*0.06}s`, flex: mobile?'1 1 100%':'1 1 300px', maxWidth: mobile?undefined:360 }}>
                  <div style={{ background:theme.app, border:`1px solid ${theme.line}`, borderRadius:18, padding:'26px 22px' }}>
                    <div style={{ width:44, height:44, borderRadius:13, background:theme.primarySoft, display:'grid', placeItems:'center', marginBottom:16 }}>
                      <IconUsers size={22} stroke={1.8} color={theme.primaryStrong}/>
                    </div>
                    <h3 style={{ fontSize:17, fontWeight:700, color:theme.ink, margin:'0 0 8px' }}>{d.name}</h3>
                    <p style={{ fontSize:14.5, lineHeight:1.6, color:theme.body, margin:0 }}>{d.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* ══ PROGRAMMES ══ */}
      {cfg.programs.length > 0 && (
        <section id='programmes' style={{ background:theme.app, padding:'90px 0 100px', borderTop:`1px solid ${theme.line}`, borderBottom:`1px solid ${theme.line}` }}>
          <Container>
            <SectionHead eyebrow="Chaque semaine" title="Nos programmes" subtitle="Les rendez-vous réguliers de la communauté." theme={theme} mobile={mobile} center={!mobile}/>
            <div style={{ display:'flex', flexWrap:'wrap', justifyContent:'center', gap:16, marginTop:44 }}>
              {cfg.programs.map((p,i) => (
                <Reveal key={i} style={{ transitionDelay:`${i*0.06}s`, flex: mobile?'1 1 100%':'1 1 400px', maxWidth: mobile?undefined:480 }}>
                  <div style={{ display:'flex', gap:16, alignItems:'flex-start', background:theme.surface, border:`1px solid ${theme.line}`, borderRadius:16, padding:'20px 22px' }}>
                    <div style={{ flexShrink:0, width:46, height:46, borderRadius:12, background:theme.gradient, display:'grid', placeItems:'center', color:'#fff', fontWeight:800, fontSize:13 }}>
                      <IconCalendar size={20} stroke={1.8} color="#fff"/>
                    </div>
                    <div>
                      <h3 style={{ fontSize:16, fontWeight:700, color:theme.ink, margin:'0 0 4px' }}>{p.name}</h3>
                      {p.schedule && <div style={{ fontSize:12.5, fontWeight:700, color:theme.primaryStrong, marginBottom:6 }}>{p.schedule}</div>}
                      <p style={{ fontSize:14, lineHeight:1.6, color:theme.body, margin:0 }}>{p.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* ══ DIFFUSION EN DIRECT ══ */}
      {cfg.live.enabled && cfg.live.url && (
        <section id='live' style={{ background:theme.dark, padding:'80px 0', position:'relative', overflow:'hidden' }}>
          <div style={{ position:'absolute', inset:0, background:`radial-gradient(60% 60% at 20% 20%,${theme.primary}30,transparent 60%)` }}/>
          <Container style={{ position:'relative' }}>
            <div style={{ textAlign:'center', maxWidth:640, margin:'0 auto 36px' }}>
              <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:8, marginBottom:16 }}>
                <span style={{ width:9, height:9, borderRadius:'50%', background:'#ef4444', display:'inline-block', animation:'pulse 1.5s infinite' }}/>
                <Eyebrow theme={theme} color="#f87171">En direct</Eyebrow>
              </div>
              <h2 style={{ fontSize:mobile?28:36, fontWeight:800, letterSpacing:'-0.02em', color:'#fff', margin:0, lineHeight:1.15 }}>{cfg.live.title}</h2>
            </div>
            <div style={{ maxWidth:900, margin:'0 auto', borderRadius:20, overflow:'hidden', boxShadow:'0 30px 70px -20px rgba(0,0,0,.5)', border:'1px solid rgba(255,255,255,.12)' }}>
              <div style={{ position:'relative', paddingTop:'56.25%', background:'#000' }}>
                <iframe
                  src={toEmbedUrl(cfg.live.url)}
                  title={cfg.live.title}
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                  style={{ position:'absolute', inset:0, width:'100%', height:'100%', border:'none' }}
                />
              </div>
            </div>
          </Container>
        </section>
      )}

      {/* ══ FAMILLES RCC ══ */}
      {landing?.use_families && families.length > 0 && (
        <section id='familles' style={{ background:theme.surface, padding:'90px 0 100px' }}>
          <Container>
            <SectionHead eyebrow="Communauté" title="Les Familles RCC" subtitle="Chaque fidèle appartient à une famille — un cercle de proximité et de croissance." theme={theme} mobile={mobile} center={!mobile}/>
            <div style={{ display:'flex', flexWrap:'wrap', justifyContent:'center', gap:14, marginTop:44 }}>
              {families.map((f,i) => (
                <Reveal key={f.id} style={{ transitionDelay:`${i*0.05}s`, width: mobile?'calc(50% - 7px)':160 }}>
                  <div style={{ textAlign:'center', background:theme.app, border:`1px solid ${theme.line}`, borderRadius:16, padding:'24px 12px' }}>
                    <div style={{ width:48, height:48, margin:'0 auto 12px', borderRadius:'50%', background:theme.gradient, display:'grid', placeItems:'center', color:'#fff', fontWeight:800, fontSize:16 }}>
                      {f.name.slice(0,2).toUpperCase()}
                    </div>
                    <div style={{ fontSize:14.5, fontWeight:700, color:theme.ink }}>{f.name}</div>
                    <div style={{ fontSize:12, color:theme.muted, marginTop:2 }}>{f.member_count} membre{f.member_count>1?'s':''}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* ══ GALERIE ══ */}
      {gallery.length > 0 && (
        <section id='galerie' style={{ background:theme.surface, padding:'90px 0 100px' }}>
          <Container>
            <SectionHead
              eyebrow="Souvenirs"
              title="Galerie de nos activités"
              subtitle="Photos, vidéos et diffusions en direct des moments partagés en famille."
              theme={theme} mobile={mobile} center={!mobile}
            />
            {(() => {
              const counts = {
                all: gallery.length,
                image: gallery.filter(m=>m.media_type==='image').length,
                video: gallery.filter(m=>m.media_type==='video').length,
                live: gallery.filter(m=>m.media_type==='live').length,
              };
              const tabs = [
                { key:'all', label:'Tout' },
                { key:'image', label:'Photos' },
                { key:'video', label:'Vidéos' },
                { key:'live', label:'Live' },
              ].filter(t => t.key==='all' || counts[t.key]>0);
              if (tabs.length <= 2) return null;
              return (
                <div style={{ display:'flex', gap:8, justifyContent: mobile?'flex-start':'center', flexWrap:'wrap', marginTop:28, overflowX: mobile?'auto':'visible' }}>
                  {tabs.map(t => (
                    <button key={t.key} onClick={()=>setGalleryFilter(t.key)}
                      style={{ padding:'8px 16px', borderRadius:999, border:`1.5px solid ${galleryFilter===t.key?theme.primary:theme.line}`, background: galleryFilter===t.key?theme.primary:'transparent', color: galleryFilter===t.key?'#fff':theme.body, fontSize:13.5, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:6 }}>
                      {t.key==='live' && <span style={{ width:7, height:7, borderRadius:'50%', background: galleryFilter===t.key?'#fff':'#ef4444', display:'inline-block' }}/>}
                      {t.label} <span style={{ opacity:.7, fontWeight:600 }}>({counts[t.key]})</span>
                    </button>
                  ))}
                </div>
              );
            })()}
            <div style={{ display:'flex', flexWrap:'wrap', justifyContent:'center', gap:14, marginTop:36 }}>
              {gallery.filter(m => galleryFilter==='all' || m.media_type===galleryFilter).slice(0, 3).map(m => (
                <div key={m.id} onClick={()=>setSelectedMedia(m)}
                  style={{ position:'relative', borderRadius:14, overflow:'hidden', width: mobile?'calc(50% - 7px)':220, aspectRatio:'1', background: m.media_type==='live' ? '#0f172a' : theme.line, cursor:'pointer' }}>
                  {m.media_type === 'live' ? (
                    m.file_url ? (
                      <img src={m.file_url} alt={m.caption || 'Diffusion en direct'} style={{ width:'100%', height:'100%', objectFit:'cover' }} loading="lazy" />
                    ) : (
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', width:'100%', height:'100%', gap:8 }}>
                        <span style={{ width:12, height:12, borderRadius:'50%', background:'#ef4444', display:'inline-block' }} />
                        <span style={{ color:'#fff', fontSize:12, fontWeight:700 }}>EN DIRECT</span>
                      </div>
                    )
                  ) : m.media_type === 'video' ? (
                    <video src={m.file_url} style={{ width:'100%', height:'100%', objectFit:'cover' }} muted playsInline />
                  ) : (
                    <img src={m.file_url} alt={m.caption || m.activity_name} style={{ width:'100%', height:'100%', objectFit:'cover' }} loading="lazy" />
                  )}
                  {m.media_type === 'live' && m.file_url && (
                    <span style={{ position:'absolute', top:8, left:8, display:'flex', alignItems:'center', gap:5, background:'rgba(239,68,68,.92)', color:'#fff', fontSize:10.5, fontWeight:800, borderRadius:6, padding:'3px 8px', letterSpacing:'0.03em' }}>
                      <span style={{ width:6, height:6, borderRadius:'50%', background:'#fff', display:'inline-block' }}/>
                      EN DIRECT
                    </span>
                  )}
                  {m.church_name && (
                    <span style={{ position:'absolute', top:8, right:8, background:'rgba(15,23,42,.72)', color:'#fff', fontSize:10, fontWeight:700, borderRadius:6, padding:'3px 8px', maxWidth:'75%', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {m.church_name}
                    </span>
                  )}
                  <div style={{ position:'absolute', left:0, right:0, bottom:0, padding:'18px 10px 8px', background:'linear-gradient(transparent, rgba(0,0,0,0.72))', color:'#fff', fontSize:11, fontWeight:600 }}>
                    {m.caption || m.activity_name || (m.media_type==='live' ? 'Diffusion en direct' : '')}
                  </div>
                </div>
              ))}
            </div>
            {gallery.length > 3 && (
              <div style={{ display:'flex', justifyContent:'center', marginTop:32 }}>
                <button onClick={()=>navigate('/galerie-publique')}
                  style={{ display:'flex', alignItems:'center', gap:8, padding:'12px 26px', borderRadius:999, border:`1.5px solid ${theme.primary}`, background:'transparent', color:theme.primaryStrong, fontSize:14.5, fontWeight:700, cursor:'pointer' }}>
                  Voir toute la galerie
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </button>
              </div>
            )}
          </Container>
        </section>
      )}

      {/* ══ STATS ══ */}
      <section style={{ background:theme.surface, position:'relative', zIndex:2 }}>
        <Container className='cm-stats-wrap' style={{ transform:'translateY(-46px)' }}>
          <div ref={statsRef} className="cm-stats-grid" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:0, background:'#fff', border:`1px solid ${theme.line}`, borderRadius:22, padding:'28px 10px', boxShadow:'0 18px 50px -20px rgba(15,23,42,.18)' }}>
            {cfg.stats.map((s,i) => <StatCell key={i} s={s} seen={statsSeen} theme={theme} divider={!mobile&&i>0}/>)}
          </div>
        </Container>
      </section>

      {/* ══ FEATURES ══ */}
      <section id='features' className='cm-section-feat' style={{ background:theme.surface, padding:'70px 0 100px' }}>
        <Container>
          <SectionHead eyebrow={cfg.features.eyebrow} title={cfg.features.title} subtitle={cfg.features.subtitle} theme={theme} mobile={mobile}/>
          <div className="cm-feat-grid" style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:18, marginTop:44 }}>
            {cfg.features.items.map((it,i) => (
              <Reveal key={i} style={{ transitionDelay:`${i*0.08}s` }}>
                <FeatCard it={it} theme={theme}/>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* ══ TÉMOIGNAGES ══ */}
      <section id='temoignages' className='cm-testi-section' style={{ background:theme.dark, padding:'100px 0', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, background:`radial-gradient(70% 50% at 80% 0%,${theme.primary}33,transparent 60%)` }}/>
        <Container style={{ position:'relative' }}>
          <div style={{ textAlign:'center', maxWidth:620, margin:'0 auto' }}>
            <div style={{ display:'flex', justifyContent:'center' }}><Eyebrow theme={theme} color={lighten(theme.primary,0.45)}>{cfg.temoignages.eyebrow}</Eyebrow></div>
            <h2 style={{ fontSize:mobile?30:40, fontWeight:800, letterSpacing:'-0.03em', color:'#fff', margin:'16px 0 0', lineHeight:1.1 }}>{cfg.temoignages.title}</h2>
          </div>
          <div className="cm-testi-grid" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:18, marginTop:50 }}>
            {(mobile ? cfg.temoignages.items.slice(0,1) : cfg.temoignages.items).map((it,i) => (
              <div key={i} style={{ position:'relative', background:'rgba(255,255,255,.045)', border:'1px solid rgba(255,255,255,.10)', borderRadius:22, padding:'30px 26px 26px' }}>
                <div style={{ position:'absolute', top:6, right:22, fontSize:96, lineHeight:1, fontWeight:800, color:lighten(theme.primary,0.2), opacity:.22, fontFamily:'Georgia,serif' }}>"</div>
                <div style={{ display:'flex', gap:3, marginBottom:16 }}>{Array.from({length:it.stars}).map((_,j)=><IconStar key={j} size={18}/>)}</div>
                <p style={{ position:'relative', fontSize:16.5, lineHeight:1.6, color:'#eef2f8', fontWeight:500, margin:'0 0 22px' }}>{it.quote}</p>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <span style={{ width:42, height:42, borderRadius:'50%', background:theme.gradient, display:'grid', placeItems:'center', color:'#fff', fontSize:15, fontWeight:700 }}>{it.name.split(' ').map(w=>w[0]).slice(0,2).join('')}</span>
                  <div>
                    <div style={{ fontSize:15, fontWeight:700, color:'#fff' }}>{it.name}</div>
                    <div style={{ fontSize:13, color:'rgba(203,213,225,.8)', marginTop:1 }}>{it.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ══ CTA FINAL ══ */}
      <section className='cm-cta-section' style={{ background:theme.surface, padding:'90px 0' }}>
        <Container>
          <Reveal className='cm-cta-box' style={{ position:'relative', overflow:'hidden', borderRadius:30, background:theme.gradient, padding:'70px 56px', textAlign:'center', boxShadow:`0 30px 60px -24px ${theme.primary}88` }}>
            <div style={{ position:'absolute', inset:0, backgroundImage:'linear-gradient(rgba(255,255,255,.10) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.10) 1px,transparent 1px)', backgroundSize:'40px 40px', opacity:.4, maskImage:'radial-gradient(70% 70% at 50% 50%,#000,transparent)', WebkitMaskImage:'radial-gradient(70% 70% at 50% 50%,#000,transparent)' }}/>
            <div style={{ position:'absolute', right:-60, top:-60, width:240, height:240, borderRadius:'50%', background:'rgba(255,255,255,.10)' }}/>
            <div style={{ position:'relative', maxWidth:620, margin:'0 auto' }}>
              <h2 className='cm-cta-h2' style={{ fontSize:44, fontWeight:800, letterSpacing:'-0.03em', color:'#fff', margin:0, lineHeight:1.08 }}>{cfg.cta.title}</h2>
              <p style={{ fontSize:mobile?16.5:18.5, lineHeight:1.55, color:'rgba(255,255,255,.9)', margin:'18px auto 0', maxWidth:500 }}>{cfg.cta.subtitle}</p>
              <div style={{ display:'flex', gap:13, justifyContent:'center', marginTop:30, flexWrap:'wrap' }}>
                <Btn theme={theme} variant="white" size="lg" icon onClick={()=>setContactType('creation_espace')}>{cfg.cta.cta1}</Btn>
                <Btn theme={theme} variant="outlineLight" size="lg" onClick={()=>setContactType('contact')}>{cfg.cta.cta2}</Btn>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* ══ FOOTER ══ */}
      <footer className='cm-footer' style={{ background:theme.dark, color:'#fff', padding:'70px 0 36px' }}>
        <Container>
          <div className="cm-footer-grid" style={{ display:'grid', gridTemplateColumns:'1.4fr repeat(3,1fr)', gap:40 }}>
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:11, marginBottom:16 }}>
                {cfg.logoUrl?<img src={cfg.logoUrl} alt="logo" style={{ width:36,height:36,borderRadius:9,objectFit:'cover' }}/>:<Logo theme={theme} size={36}/>}
                <span style={{ fontSize:18, fontWeight:700, color:'#fff', letterSpacing:'-0.02em' }}>{cfg.brand}</span>
              </div>
              <p style={{ fontSize:14.5, lineHeight:1.6, color:'rgba(203,213,225,.78)', margin:0, maxWidth:290 }}>{cfg.footer.tagline}</p>
            </div>
            {cfg.footer.columns.map((col,i) => (
              <div key={i} className="cm-footer-cols">
                <div style={{ fontSize:12, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(203,213,225,.55)', marginBottom:16 }}>{col.title}</div>
                <div style={{ display:'flex', flexDirection:'column', gap:11 }}>
                  {col.links.map(l=><span key={l} style={{ fontSize:14.5, color:'rgba(203,213,225,.78)', cursor:'default' }}>{l}</span>)}
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop:44, paddingTop:24, borderTop:'1px solid rgba(255,255,255,.10)', display:'flex', flexWrap:'wrap', gap:14, justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:13.5, color:'rgba(203,213,225,.6)' }}>{cfg.footer.legal}</span>
          </div>
        </Container>
      </footer>

      {/* Modales */}
      {modalDest && <LoginModal destination={modalDest} theme={theme} onClose={()=>setModalDest(null)} onSuccess={()=>{setModalDest(null);navigate(modalDest==='admin'?'/dashboard':'/accueil-mobile');}}/>}
      {contactType && <ContactModal subjectType={contactType} theme={theme} onClose={()=>setContactType(null)}/>}
      {selectedAct && <ActivityModal act={selectedAct} theme={theme} user={user} navigate={navigate} onClose={()=>setSelectedAct(null)} onLoginRequest={(dest)=>{setSelectedAct(null);setModalDest(dest);}}/>}
      {selectedNews && <NewsModal item={selectedNews} theme={theme} onClose={()=>setSelectedNews(null)}/>}
      {selectedMedia && (
        <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,.85)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', padding: mobile?12:24 }}
          onClick={()=>setSelectedMedia(null)}>
          <div onClick={e=>e.stopPropagation()}
            style={{ background:'#fff', borderRadius:18, width:'100%', maxWidth:960, maxHeight:'88vh', overflow:'hidden', display:'flex', flexDirection: mobile?'column':'row', boxShadow:'0 30px 80px rgba(0,0,0,.4)' }}>
            <div style={{ flex: mobile?'none':'1.1', background:'#0f172a', display:'flex', alignItems:'center', justifyContent:'center', minHeight: mobile?220:'auto', maxHeight: mobile?260:'88vh' }}>
              {selectedMedia.media_type === 'video' ? (
                <video src={selectedMedia.file_url} controls autoPlay style={{ width:'100%', height:'100%', maxHeight: mobile?260:'88vh', objectFit:'contain' }}/>
              ) : (
                <img src={selectedMedia.file_url} alt={selectedMedia.caption||selectedMedia.activity_name} style={{ width:'100%', height:'100%', maxHeight: mobile?260:'88vh', objectFit:'contain' }}/>
              )}
            </div>
            <div style={{ flex:1, padding: mobile?20:32, overflowY:'auto', display:'flex', flexDirection:'column' }}>
              <button onClick={()=>setSelectedMedia(null)} style={{ alignSelf:'flex-end', background:theme.app, border:'none', borderRadius:8, width:30, height:30, cursor:'pointer', color:theme.body, fontSize:15, marginBottom:12 }}>✕</button>
              <h2 style={{ fontSize: mobile?19:23, fontWeight:800, color:theme.ink, margin:'0 0 10px' }}>
                {selectedMedia.caption || selectedMedia.activity_name || 'Sans titre'}
              </h2>
              {selectedMedia.activity_name && selectedMedia.activity_name !== selectedMedia.caption && (
                <div style={{ fontSize:13.5, fontWeight:700, color:theme.primaryStrong, marginBottom:4 }}>
                  {selectedMedia.activity_name}
                </div>
              )}
              {selectedMedia.church_name && (
                <div style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:12, fontWeight:600, color:theme.muted, background:theme.app, borderRadius:999, padding:'4px 11px', width:'fit-content', marginBottom:12 }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M3 9l9-7 9 7v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"/></svg>
                  {selectedMedia.church_name}
                </div>
              )}
              {selectedMedia.description && (
                <p style={{ fontSize:14.5, lineHeight:1.7, color:theme.body, margin:'16px 0 20px', whiteSpace:'pre-line' }}>
                  {selectedMedia.description}
                </p>
              )}
              {selectedMedia.external_url && (
                <a href={selectedMedia.external_url} target="_blank" rel="noreferrer"
                  style={{ marginTop:'auto', display:'inline-flex', alignItems:'center', gap:8, padding:'11px 18px', background:theme.primary, color:'#fff', borderRadius:10, fontWeight:700, fontSize:14, textDecoration:'none', width:'fit-content' }}>
                  Voir le lien
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
